// Store del Portal Tickets de Seguimiento conectado a Supabase (tablas tickets_*).
// Mantiene el MISMO contrato que consumía la UI con datos mock (useTickets()), pero por
// debajo lee/escribe con React Query. Los ids se exponen como string al front y se
// convierten a integer al escribir en BD. La asignación (propietarioId) usa auth_user_id.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useActivityLogger } from "@/hooks/useActivityLogger";
import { toast } from "sonner";
import type {
  Agente,
  Categoria,
  ContactoRef,
  Etapa,
  FuenteTicket,
  Pipeline,
  PropiedadRef,
  Ticket,
} from "./tickets-data";
import {
  FUENTES_RESPALDO,
  MENSAJE_PIPELINE_SOLO_APPS,
  claveFuente,
  etiquetaFuente as etiquetaDeFuente,
} from "./tickets-data";
import { saveTicketAdjuntos, uploadTicketFile, type PendingAdjunto } from "./tickets-adjuntos";
import { requiereSeguimiento } from "./resolucion";
import {
  TOAST_NO_RETROCEDE,
  atendidoPor,
  esErrorNoRetrocede,
  esEtapaTerminal,
  mensajeErrorTicketRelacionado,
  etapaAtencion,
  puedeMoverA,
  type TicketReferido,
  type TicketRelacionado,
} from "./estado";
import { fetchPropiedadesDeTickets, sincronizarPropiedadesTicket } from "./tickets-propiedades";
import { resumenTexto } from "./resumen-texto";
import { bandejaDesdeSelector, cruzaBandejaAtencion, mensajeErrorReclasificar } from "./bandeja-atencion";

/** Lo que se deja al resolver un ticket: mensaje, evidencia o ambos (al menos uno). */
export type SeguimientoResolucion = {
  texto: string;
  archivos: PendingAdjunto[];
  /** true = también lo ve el cliente en su app. */
  visibleCliente?: boolean;
};

// Las tablas tickets_* no están en los tipos generados de Supabase → cast puntual.
const sb = supabase as any;

/** Tablas cuyo cambio recarga la lista (deben estar en la publicación `supabase_realtime`). */
const TABLAS_TIEMPO_REAL = ["tickets", "tickets_actividad", "tickets_propietarios", "tickets_adjuntos"] as const;

// ─── Correos de tickets (fire-and-forget) ───────────────────────────────────────
// Estándar del ecosistema SOZU: notificar-tickets -> enviar-notificacion (n8n) + template Postmark
// 41353048 - mismo patrón que crm-recordatorios-tareas. Nunca bloquea ni hace fallar
// la operación. Reutilizados por el store (Portal Tickets) y por la ficha del CRM.
export type CorreoTicketInfo = {
  folio: number | string;
  nombre: string;
  pipeline?: string | null;
  proyecto?: string | null;
  descripcion?: string | null;
  por?: string | null; // quién asignó / cerró
};

type Destinatario = { email?: string | null; nombre?: string | null; telefono?: string | null };

const escHtml = (s?: string | number | null) =>
  String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Tabla del cuerpo del correo: Ticket, Nombre, Pipeline, (Asignado/Resuelto) por, Proyecto, Descripción.
/** Cola del resumen de la descripción en correos. */
const VER_EN_PORTAL = "(completo en el Portal Tickets)";

function detallesHtml(info: CorreoTicketInfo, porLabel: string): string {
  const row = (label: string, val: string) =>
    `<tr><td style="padding:6px 12px;color:#6b7280;white-space:nowrap;vertical-align:top;">${label}</td>` +
    `<td style="padding:6px 12px;">${val}</td></tr>`;
  return [
    row("Ticket", `#${escHtml(info.folio)}`),
    row("Nombre del ticket", escHtml(info.nombre) || "Sin nombre"),
    info.pipeline ? row("Pipeline", escHtml(info.pipeline)) : "",
    info.por ? row(porLabel, escHtml(info.por)) : "",
    row("Proyecto", info.proyecto ? escHtml(info.proyecto) : "Sin proyecto"),
    // Solo un resumen: hay descripciones de páginas enteras; el detalle se lee en el portal.
    info.descripcion ? row("Resumen", `${escHtml(resumenTexto(info.descripcion))} ${VER_EN_PORTAL}`) : "",
  ].join("");
}

/** Fila de Comunicación > Correos del sistema de los avisos de tickets. */
const CLAVE_CORREO_SISTEMA = "tickets_y_chat_cliente";

/** Lo que el correo dice en la Plantilla base SOZU (si su interruptor está en 'base'). */
type CorreoTicketBase = { titulo: string; resumen: string; filas: [string, string | null | undefined][] };

/** Filas del ticket en texto, igual que la tabla del correo de hoy. */
function filasTicket(info: CorreoTicketInfo, porLabel: string): [string, string | null | undefined][] {
  return [
    ["Ticket", `#${info.folio}`],
    ["Nombre del ticket", info.nombre || "Sin nombre"],
    ["Pipeline", info.pipeline],
    [porLabel, info.por],
    ["Proyecto", info.proyecto || "Sin proyecto"],
    ["Resumen", info.descripcion ? `${resumenTexto(info.descripcion)} ${VER_EN_PORTAL}` : null],
  ];
}

/**
 * `correo_sistema` del payload: enviar-notificacion lo usa solo si el correo está en 'base' (y
 * entonces n8n solo manda el WhatsApp); con 'legado' sale exactamente lo de siempre.
 */
function correoSistemaTicket(asunto: string, base: CorreoTicketBase, nombre: string | null | undefined) {
  const detalle = base.filas
    .filter(([, v]) => String(v ?? "").trim() !== "")
    .map(([k, v]) => `${k}: ${String(v).trim()}`)
    .join("\n");
  return {
    clave: CLAVE_CORREO_SISTEMA,
    vars: { asunto, titulo: base.titulo, resumen: base.resumen, detalle, accion: "" },
    nombre: nombre ?? null,
    enlace: null,
  };
}

function enviarCorreoTicket(
  destinatarios: Destinatario[],
  asunto: string,
  actividad: string,
  detalles: string,
  mensajeWA: string,
  base: CorreoTicketBase,
) {
  for (const dest of destinatarios) {
    if (!dest?.email && !dest?.telefono) continue;
    // El template dice "Hola {nombre}, se ha realizado la {actividad}." → actividad = frase nominal
    // (ej. "resolución del ticket #1006: …"), NO una oración completa.
    const modelo = { nombre: dest.nombre || "Equipo", actividad, detalles };
    const conWA = !!dest.telefono;
    sb.functions
      .invoke("notificar-tickets", {
        body: {
          // "ambos" = correo + WhatsApp (Evolution vía n8n); sin teléfono, solo correo.
          tipo: conWA ? "ambos" : "email",
          from: "Notificaciones Sozu <notificaciones@sozu.com>",
          email: dest.email,
          ...(conWA ? { telefono: dest.telefono, mensajeWA } : {}),
          asunto,
          mensaje: modelo,
          templateId: 41353048,
          templateModel: modelo,
          correo_sistema: correoSistemaTicket(asunto, base, dest.nombre),
        },
      })
      .catch(() => {
        /* fire-and-forget: la notificación no debe romper el flujo */
      });
  }
}

// Cuerpo del mensaje de WhatsApp (texto plano; admite *negritas*).
function textoWa(info: CorreoTicketInfo, encabezado: string, porLabel: string): string {
  return [
    `*${encabezado} #${info.folio}*`,
    info.nombre || "",
    info.pipeline ? `Pipeline: ${info.pipeline}` : "",
    info.por ? `${porLabel}: ${info.por}` : "",
    info.proyecto ? `Proyecto: ${info.proyecto}` : "",
  ]
    .filter(Boolean)
    .join("\n");
}

// Correo (+ WhatsApp si el destinatario tiene teléfono) "ticket asignado" a un usuario.
export function enviarCorreoAsignacion(destinatarios: Destinatario[], info: CorreoTicketInfo) {
  const asunto = `Ticket #${info.folio} asignado: ${info.nombre}`;
  enviarCorreoTicket(
    destinatarios,
    asunto,
    `Asignación del ticket #${info.folio}: ${info.nombre}`,
    detallesHtml(info, "Asignado por"),
    textoWa(info, "Ticket asignado", "Asignado por"),
    {
      titulo: `Te asignaron el ticket #${info.folio}`,
      resumen: "Revísalo en el Portal Tickets para darle seguimiento.",
      filas: filasTicket(info, "Asignado por"),
    },
  );
}

// Correo (+ WhatsApp si hay teléfono) "ticket cerrado" a propietarios + creador.
export function enviarCorreoCierre(destinatarios: Destinatario[], info: CorreoTicketInfo) {
  const asunto = `Ticket #${info.folio} resuelto: ${info.nombre}`;
  enviarCorreoTicket(
    destinatarios,
    asunto,
    `Resolución del ticket #${info.folio}: ${info.nombre}`,
    detallesHtml(info, "Resuelto por"),
    textoWa(info, "Ticket resuelto", "Resuelto por"),
    {
      titulo: `Ticket #${info.folio} resuelto`,
      resumen: "El ticket quedó resuelto. Puedes ver el detalle en el Portal Tickets.",
      filas: filasTicket(info, "Resuelto por"),
    },
  );
}

// Correo "nueva nota" a propietarios + creador (menos el autor de la nota). Solo
// correo por decisión de producto: las notas son frecuentes y saturarían el WhatsApp.
export function enviarCorreoNota(destinatarios: Destinatario[], info: CorreoTicketInfo, nota: string) {
  const asunto = `Ticket #${info.folio} - nueva nota: ${info.nombre}`;
  const filaNota =
    `<tr><td style="padding:6px 12px;color:#6b7280;white-space:nowrap;vertical-align:top;">Nota</td>` +
    `<td style="padding:6px 12px;">${escHtml(nota)}</td></tr>`;
  enviarCorreoTicket(
    destinatarios,
    asunto,
    `Nueva nota en el ticket #${info.folio}: ${info.nombre}`,
    detallesHtml(info, "Nota de") + filaNota,
    textoWa(info, "Nueva nota", "Nota de"),
    {
      titulo: `Nueva nota en el ticket #${info.folio}`,
      resumen: "Hay una nota nueva en un ticket en el que participas.",
      filas: [...filasTicket(info, "Nota de"), ["Nota", nota]],
    },
  );
}

export type NuevoTicket = {
  nombre: string;
  pipelineId: string;
  etapaId: string;
  prioridad: Ticket["prioridad"];
  categoriaId: string;
  propietarios: string[];
  solicitante: string;
  solicitantes?: ContactoRef[];
  inmueble: string;
  descripcion: string;
  fuente?: string;
  fechaCreacion?: string;
  entidadRelacionadaId?: string | null;
  propiedadId?: string | null;
  /** tickets.id_proyecto (estructurado; `inmueble` sigue siendo el nombre en texto). */
  proyectoId?: string | null;
  /** tickets.id_cuenta_cobranza: solo se escribe si la columna ya existe en la BD. */
  cuentaCobranzaId?: string | null;
  /** tickets.id_ticket_relacionado: ticket anterior (abierto o cerrado) al que hace referencia. */
  ticketRelacionadoId?: string | null;
  /**
   * Propiedades ligadas (tickets_propiedades). La primera llena id_propiedad / id_cuenta_cobranza /
   * id_proyecto / inmueble cuando esos campos no vienen.
   */
  propiedades?: PropiedadRef[];
  adjuntos?: PendingAdjunto[];
};

/** Lo que regresa la BD al crear un ticket: con esto se arma su resumen sin buscarlo. */
export type TicketCreado = {
  id: string;
  numero: number;
  /** tickets.fecha_creacion tal como quedó guardada. */
  fechaCreacion: string;
};

/**
 * Alcance del provider cuando se monta FUERA del Portal Tickets (Portal Cobranza, Portal
 * Productos). Sin alcance = comportamiento del Portal Tickets: carga todos los tickets.
 */
export type AlcanceTickets = {
  /**
   * Pipelines destino del portal. Con alcance, el servidor solo devuelve los tickets de estos
   * pipelines + los que levantó el usuario + los asignados a él. `[]` = el portal aún no tiene
   * destinos: solo los propios.
   */
  pipelineIds?: number[];
  /**
   * CLAVE de `tickets_fuentes` que se guarda en tickets.fuente al crear (p. ej. FUENTE.portalCobranza).
   * Nunca el nombre del menú.
   */
  fuente?: string;
  /**
   * Rutas extra cuyos roles entran al pool de asignables (además de /admin/portal-tickets/%),
   * para poder asignar y notificar a la gente del portal que no entra al Portal Tickets.
   */
  rutasAgentes?: string[];
  /** Categoría por defecto de cada pipeline destino (id pipeline → id categoría) al crear. */
  categoriaPorPipeline?: Record<string, string>;
  /**
   * false = no carga la lista de tickets (solo catálogos y agentes). Para montar solo el
   * diálogo de crear (LevantarTicketButton) sin bajar la mesa.
   */
  cargarTickets?: boolean;
  /**
   * Menú del portal (19 Cobranza, 36 Productos) cuya página Tickets muestra SOLO los tickets
   * del usuario: los que levantó o los que tiene asignados. Con valor, la lista sale de la RPC
   * `get_tickets_portal_propios` (el filtro vive en el servidor) y `pipelineIds` solo acota a
   * dónde se crea. Sin la RPC (migración pendiente) cae al mismo filtro armado aquí.
   */
  propiosMenuId?: number;
  /**
   * Pipelines que se ven en la página (destinos activos del portal, creen o no). Con
   * `propiosMenuId` (o `soloPipelinesVisibles`), la lista y el selector de pipeline se limitan a estos.
   */
  pipelinesVisibles?: number[];
  /**
   * Tickets del Portal Condominio (Ticket #1481): la lista es SOLO la de `pipelinesVisibles` (el
   * pipeline del condominio elegido), sin sumar lo que el usuario levantó o tiene asignado en
   * otras áreas.
   */
  soloPipelinesVisibles?: boolean;
  /**
   * Condominio (proyecto) cuyo equipo es el pool de asignables (`fetchAgentesCondominio`), en vez
   * de los roles del Portal Tickets + `rutasAgentes`.
   */
  asignablesProyectoId?: number;
};

type Store = {
  /**
   * Ventana de tickets cerrados que se cargaron (regla de Eduardo, 2026-10-27): los abiertos van
   * siempre completos; de los cerrados (con `fecha_cierre`) solo los del mes en curso, y cada
   * `verMasCerrados()` baja un mes más. `cerradosDesde` null = todos (lo pidió un filtro).
   */
  cerradosDesde: string | null;
  hayMasCerrados: boolean;
  verMasCerrados: () => void;
  /** Un filtro que pide todo (p. ej. rango de creación) carga todos los cerrados. */
  cargarTodosCerrados: (todos: boolean) => void;
  /**
   * "Ver como" (la lista es de otro usuario): solo lectura. Las escrituras del store quedan
   * sustituidas por un aviso y la UI apaga crear, mover, editar, notas y chat.
   */
  soloLectura: boolean;
  tickets: Ticket[];
  pipelines: Pipeline[];
  etapas: Etapa[];
  categorias: Categoria[];
  agentes: Agente[];
  /** Catálogo `tickets_fuentes` (respaldo local mientras carga o si la tabla no existe). */
  fuentes: FuenteTicket[];
  /** Etiqueta de una fuente (clave o texto viejo). */
  etiquetaFuente: (clave: string | null | undefined) => string;
  autor: string;
  cargando: boolean;
  /**
   * Recarga suave (DESIGN §3): cambió la vista o el alcance y se están pidiendo los tickets nuevos
   * mientras se muestran los anteriores. La pantalla los atenúa en vez de volver al "Cargando…".
   */
  recargando: boolean;
  /** La lista de tickets no se pudo leer (red/RLS): no es lo mismo que "no hay tickets". */
  errorCarga: boolean;
  recargar: () => void;
  /** Alcance con el que se montó el provider (vacío en el Portal Tickets). */
  alcance: AlcanceTickets;
  /** El ticket creado, o `null` si no se creó (el error ya se avisó con un toast). */
  crearTicket: (t: NuevoTicket) => Promise<TicketCreado | null>;
  /** Resuelve `true` si todo quedó guardado (cada error ya se avisó con su toast). */
  actualizarTicket: (id: string, cambios: Partial<Ticket>, nota?: string) => Promise<boolean>;
  /**
   * Deja ligadas exactamente `propiedades` (la primera = principal en tickets.id_propiedad).
   * Resuelve `true` si quedó guardado.
   */
  guardarPropiedadesTicket: (id: string, propiedades: PropiedadRef[]) => Promise<boolean>;
  /**
   * «Guardar cambios» del detalle en UNA petición (RPC `actualizar_ticket_completo`): solo se
   * mandan las llaves que cambiaron. Si la RPC aún no está en el ambiente (PGRST202 / 42883) cae a
   * la cadena de siempre. Resuelve `true` si todo quedó guardado (cada error ya se avisó).
   */
  guardarTicketCompleto: (id: string, cambios: CambiosTicketCompleto, nota?: string) => Promise<boolean>;
  agregarPropiedadTicket: (id: string, propiedad: PropiedadRef) => Promise<boolean>;
  quitarPropiedadTicket: (id: string, propiedadId: string) => Promise<boolean>;
  /**
   * Liga el ticket con otro (`tickets.id_ticket_relacionado`) o quita la liga (`relacionado` null).
   * Resuelve `true` si quedó guardado.
   */
  relacionarTicket: (id: string, relacionado: { id: string; numero: number } | null) => Promise<boolean>;
  /** Resolver (entrar a una etapa terminal desde una de trabajo) exige `seguimiento`. */
  moverEtapa: (id: string, etapaId: string, seguimiento?: SeguimientoResolucion) => Promise<boolean>;
  /** «Yo trabajo el ticket» (true) o soltarlo (false). Resuelve `true` si quedó guardado. */
  tomarTicket: (id: string, tomar: boolean) => Promise<boolean>;
  /** Resuelve `true` solo si el ticket quedó en el pipeline nuevo. */
  cambiarPipeline: (id: string, pipelineId: string) => Promise<boolean>;
  /** El equipo leyó la Conversación (palomitas verdes para el cliente/agente/broker). */
  marcarLeidoEquipo: (id: string) => Promise<void>;
  eliminarTickets: (ids: string[]) => Promise<boolean>;
  /** Resuelve `true` solo si la nota quedó guardada: la UI limpia el borrador con eso. */
  agregarNota: (
    id: string,
    texto: string,
    audioFile?: File,
    paraCliente?: boolean,
  ) => Promise<boolean>;
  guardarPipeline: (p: Pipeline) => void;
  eliminarPipeline: (id: string) => void;
  guardarEtapa: (e: Etapa) => void;
  eliminarEtapa: (id: string) => void;
  guardarCategoria: (c: Categoria) => void;
  eliminarCategoria: (id: string) => void;
  guardarAgente: (a: Agente) => void;
  eliminarAgente: (id: string) => void;
};

const TicketsContext = createContext<Store | null>(null);

export const AVISO_VER_COMO_SOLO_LECTURA =
  "Estás viendo como otro usuario: la vista es de solo lectura. Sal de «Ver como» para hacer cambios.";

/**
 * Store de "Ver como": se ve lo mismo que el impersonado, pero nada escribe. Cada escritura avisa y
 * no hace nada (marcar leído es automático: se omite en silencio, el impersonado no leyó nada).
 */
function conEscriturasBloqueadas(s: Store): Store {
  const aviso = () => {
    toast.info(AVISO_VER_COMO_SOLO_LECTURA);
  };
  const bloqueada = async () => {
    aviso();
    return false;
  };
  return {
    ...s,
    soloLectura: true,
    crearTicket: async () => {
      aviso();
      return null;
    },
    actualizarTicket: bloqueada,
    guardarPropiedadesTicket: bloqueada,
    guardarTicketCompleto: bloqueada,
    agregarPropiedadTicket: bloqueada,
    quitarPropiedadTicket: bloqueada,
    relacionarTicket: bloqueada,
    moverEtapa: bloqueada,
    tomarTicket: bloqueada,
    cambiarPipeline: bloqueada,
    marcarLeidoEquipo: async () => {},
    eliminarTickets: bloqueada,
    agregarNota: bloqueada,
    guardarPipeline: aviso,
    eliminarPipeline: aviso,
    guardarEtapa: aviso,
    eliminarEtapa: aviso,
    guardarCategoria: aviso,
    eliminarCategoria: aviso,
    guardarAgente: aviso,
    eliminarAgente: aviso,
  };
}

// ─── Fetchers ─────────────────────────────────────────────────────────────────
/** Catálogo `tickets_fuentes`. Sin la tabla (migración pendiente) cae al respaldo local. */
export async function fetchFuentes(): Promise<FuenteTicket[]> {
  const { data, error } = await sb
    .from("tickets_fuentes")
    .select("clave, etiqueta, manual, orden, activo, alias")
    .order("orden", { ascending: true });
  if (error || !data?.length) return FUENTES_RESPALDO;
  return (data as any[]).map((r) => ({
    clave: r.clave,
    etiqueta: r.etiqueta ?? r.clave,
    manual: !!r.manual,
    orden: r.orden ?? 0,
    activo: r.activo !== false,
    alias: Array.isArray(r.alias) ? r.alias : [],
  }));
}

async function fetchPipelines(): Promise<Pipeline[]> {
  const [{ data, error }, condominio] = await Promise.all([
    sb
      .from("tickets_pipelines")
      // select("*"): `solo_apps` y `mostrar_asistente` llegan solo si su migración ya corrió.
      .select("*")
      .eq("activo", true)
      .order("orden"),
    // Pipelines de condominio (Ticket #1481). Sin la tabla (migración pendiente) o con error, ninguno:
    // solo apaga la pestaña Conversación de esos pipelines, nunca tumba el tablero.
    sb.from("tickets_pipelines_condominio").select("id_pipeline").eq("activo", true),
  ]);
  if (error) throw error;
  const deCondominio = new Set<string>(
    condominio.error ? [] : (condominio.data ?? []).map((r: any) => String(r.id_pipeline)),
  );
  return (data ?? []).map((r: any) => ({
    id: String(r.id),
    nombre: r.nombre,
    descripcion: r.descripcion ?? "",
    codigo: r.codigo ?? null,
    ambito: r.ambito ?? null,
    soloApps: r.solo_apps === true,
    mostrarAsistente: typeof r.mostrar_asistente === "boolean" ? r.mostrar_asistente : undefined,
    deCondominio: deCondominio.has(String(r.id)),
  }));
}

async function fetchEtapas(): Promise<Etapa[]> {
  const { data, error } = await sb
    .from("tickets_etapas")
    .select("id, id_pipeline, nombre, orden, cerrada, tipo_semantico")
    .eq("activo", true)
    .order("id_pipeline")
    .order("orden");
  if (error) throw error;
  return (data ?? []).map((r: any) => ({
    id: String(r.id),
    pipelineId: String(r.id_pipeline),
    nombre: r.nombre,
    orden: r.orden,
    cerrada: r.cerrada,
    tipoSemantico: r.tipo_semantico ?? null,
  }));
}

async function fetchCategorias(): Promise<Categoria[]> {
  const { data, error } = await sb
    .from("tickets_categorias")
    // select("*"): `bandeja_atencion` llega solo si su migración ya corrió; sin ella queda en null.
    .select("*")
    .eq("activo", true)
    .order("orden");
  if (error) throw error;
  return (data ?? []).map((r: any) => ({
    id: String(r.id),
    nombre: r.nombre,
    pipelineId: r.id_pipeline != null ? String(r.id_pipeline) : null,
    bandejaAtencion: bandejaDesdeSelector(r.bandeja_atencion ?? ""),
  }));
}

// Número para WhatsApp = lada de país + teléfono (solo dígitos). `clave_pais_telefono` puede
// venir como número ("+52"/"52") o como ISO ("MX"); si es ISO se mapea a lada (default México=52).
// Sin esto, "MX" perdía la lada al quitar los no-dígitos y el WhatsApp no se entregaba.
const LADA_POR_ISO: Record<string, string> = {
  MX: "52", US: "1", CA: "1", ES: "34", CO: "57", AR: "54", PE: "51", CL: "56", GT: "502",
};
function numeroWA(u: { telefono?: string | null; clave_pais_telefono?: string | null }): string | null {
  const tel = String(u.telefono ?? "").replace(/\D/g, "");
  if (!tel) return null;
  const claveRaw = String(u.clave_pais_telefono ?? "").trim().toUpperCase();
  let lada = claveRaw.replace(/\D/g, ""); // "+52" → "52"
  if (!lada) lada = LADA_POR_ISO[claveRaw] ?? "52"; // "MX" → "52"; default México
  return `${lada}${tel}`;
}

/**
 * Asignables del menú Tickets del Portal Condominio (Ticket #1481): el equipo con acceso a ese
 * condominio (RPC `get_tickets_asignables_condominio`). La administración del condominio no lee
 * `usuarios` (no es staff) y al staff no se le ofrece la administración de otro condominio, que
 * vería el ticket al asignárselo. Sin la RPC (migración pendiente), el pool de siempre.
 */
async function fetchAgentesCondominio(idProyecto: number, rutasExtra: string[]): Promise<Agente[]> {
  const { data, error } = await sb.rpc("get_tickets_asignables_condominio", { p_id_proyecto: idProyecto });
  if (error) {
    if (error.code === "PGRST202" || error.code === "42883") return fetchAgentesConRutas(rutasExtra);
    throw error;
  }
  return (data ?? [])
    .filter((u: any) => u.auth_user_id)
    .map((u: any) => ({
      id: u.auth_user_id,
      nombre: u.nombre,
      email: u.email,
      rol: u.rol ?? "",
      telefono: numeroWA(u),
    }));
}

// Pool de asignables = usuarios activos cuyo rol tiene acceso a alguna ruta del portal
// de tickets (mismo criterio que fetchCrmOwners; se deriva de BD para no hardcodear roles).
// Exportada para reusarla en el selector "Ver como" (impersonación).
// Firma sin argumentos a propósito: varios useQuery la pasan tal cual como queryFn.
export function fetchAgentes(): Promise<Agente[]> {
  return fetchAgentesConRutas([]);
}

/** Pool de asignables + los roles con acceso a `rutasExtra` (portales que montan tickets). */
export async function fetchAgentesConRutas(rutasExtra: string[]): Promise<Agente[]> {
  let rolIds: number[] = [];
  const { data: subs, error: subsErr } = await sb
    .from("submenus")
    .select("id")
    .like("vista_front_end", "/admin/portal-tickets/%");
  if (subsErr) throw subsErr;
  const submenuIds = (subs ?? []).map((s: any) => s.id);
  // Portales que montan tickets fuera del portal (Cobranza, Productos): sus roles también son
  // asignables, si no el responsable de la categoría no aparecería ni recibiría el correo.
  if (rutasExtra.length) {
    const { data: extra, error: extraErr } = await sb
      .from("submenus")
      .select("id")
      .in("vista_front_end", rutasExtra);
    if (extraErr) throw extraErr;
    for (const x of extra ?? []) if (!submenuIds.includes(x.id)) submenuIds.push(x.id);
  }
  if (submenuIds.length) {
    const { data: perms, error: permsErr } = await sb
      .from("submenus_permisos")
      .select("rol_id")
      .in("submenu_id", submenuIds)
      .eq("activo", true);
    if (permsErr) throw permsErr;
    rolIds = Array.from(new Set((perms ?? []).map((p: any) => p.rol_id)));
  }
  if (!rolIds.length) rolIds = [1]; // fallback: Super Admin

  const [usRes, rolesRes] = await Promise.all([
    sb.from("usuarios").select("auth_user_id, nombre, email, rol_id, telefono, clave_pais_telefono").eq("activo", true).in("rol_id", rolIds),
    sb.from("roles").select("id, nombre"),
  ]);
  // Si estas consultas fallan (red/RLS), lanzar el error en vez de devolver [] silenciosamente:
  // así React Query reintenta y marca la carga como fallida, en vez de cachear una lista vacía
  // que se ve idéntica a "no hay usuarios asignables".
  if (usRes.error) throw usRes.error;
  if (rolesRes.error) throw rolesRes.error;
  const us = usRes.data;
  const roles = rolesRes.data;
  const rolMap = new Map((roles ?? []).map((r: any) => [r.id, r.nombre]));
  return (us ?? [])
    .filter((u: any) => u.auth_user_id)
    .map((u: any) => ({
      id: u.auth_user_id,
      nombre: u.nombre,
      email: u.email,
      rol: rolMap.get(u.rol_id) ?? "",
      telefono: numeroWA(u),
    }))
    .sort((a: Agente, b: Agente) => (a.nombre ?? "").localeCompare(b.nombre ?? ""));
}

// Teléfono legible para el popover de solicitantes: prefija la lada solo si clave_pais_telefono
// parece número (+52), no cuando es un ISO ("MX"). Devuelve null si no hay teléfono.
function telefonoDisplay(p: { telefono?: string | null; clave_pais_telefono?: string | null }): string | null {
  const tel = String(p.telefono ?? "").trim();
  if (!tel) return null;
  const clave = String(p.clave_pais_telefono ?? "").trim();
  const lada = /^\+?\d+$/.test(clave) ? `+${clave.replace(/^\+/, "")} ` : "";
  return `${lada}${tel}`;
}

// DDL probe de tickets.id_cuenta_cobranza (migración de tickets por portal). Se pregunta una
// vez por sesión: sin la columna, ni se lee ni se escribe y todo lo demás sigue igual.
let columnaCuentaCobranza: Promise<boolean> | null = null;
export function existeColumnaCuentaCobranza(): Promise<boolean> {
  if (!columnaCuentaCobranza) {
    columnaCuentaCobranza = sb
      .from("tickets")
      .select("id_cuenta_cobranza")
      .limit(0)
      .then(({ error }: { error: unknown }) => !error, () => false);
  }
  return columnaCuentaCobranza as Promise<boolean>;
}

// DDL probe de tickets.id_ticket_relacionado (ticket #1360, 20261020130000). Sin la columna no
// se puede guardar la relación con un ticket anterior.
let columnaTicketRelacionado: Promise<boolean> | null = null;
function existeColumnaTicketRelacionado(): Promise<boolean> {
  if (!columnaTicketRelacionado) {
    columnaTicketRelacionado = sb
      .from("tickets")
      .select("id_ticket_relacionado")
      .limit(0)
      .then(({ error }: { error: unknown }) => !error, () => false);
  }
  return columnaTicketRelacionado as Promise<boolean>;
}

/**
 * Ticket por su folio (`numero`), con su etapa, para validar la relación al crear. null = no
 * existe o no se ve (RLS). Lanza si la consulta falla, para no confundirlo con «no existe».
 */
export async function buscarTicketPorFolio(numero: number): Promise<TicketReferido | null> {
  const { data: tk, error } = await sb
    .from("tickets")
    .select("id, numero, nombre, activo, id_etapa, id_pipeline, descripcion, solicitante, inmueble, fecha_creacion, fecha_cierre")
    .eq("numero", numero)
    .maybeSingle();
  if (error) throw error;
  if (!tk) return null;
  const { data: etapa, error: etapaErr } = await sb
    .from("tickets_etapas")
    .select("nombre, cerrada")
    .eq("id", tk.id_etapa)
    .maybeSingle();
  if (etapaErr) throw etapaErr;
  return {
    id: String(tk.id),
    numero: tk.numero,
    nombre: tk.nombre,
    activo: !!tk.activo,
    cerrado: !!etapa?.cerrada,
    etapa: etapa?.nombre ?? "sin etapa",
    descripcion: tk.descripcion ?? null,
    solicitante: tk.solicitante ?? null,
    inmueble: tk.inmueble ?? null,
    pipelineId: tk.id_pipeline != null ? String(tk.id_pipeline) : null,
    fechaCreacion: tk.fecha_creacion ?? null,
    fechaCierre: tk.fecha_cierre ?? null,
  };
}

/**
 * Tickets relacionados con `id` por `id_ticket_relacionado`, en las dos direcciones: el anterior al
 * que apunta y los posteriores que apuntan a él. Solo activos y los que la RLS deja ver. Sin la
 * columna en el ambiente, vacío. `etapas` da el nombre y si la etapa es terminal.
 */
export async function buscarTicketsRelacionados(id: string, etapas: Etapa[]): Promise<TicketRelacionado[]> {
  if (!(await existeColumnaTicketRelacionado())) return [];
  const cols = "id, numero, nombre, id_etapa, id_pipeline";
  const { data: propio, error } = await sb.from("tickets").select("id_ticket_relacionado").eq("id", Number(id)).maybeSingle();
  if (error) throw error;
  const anteriorId = propio?.id_ticket_relacionado;
  const [anterior, posteriores] = await Promise.all([
    anteriorId != null
      ? sb.from("tickets").select(cols).eq("id", anteriorId).eq("activo", true)
      : Promise.resolve({ data: [], error: null }),
    sb.from("tickets").select(cols).eq("id_ticket_relacionado", Number(id)).eq("activo", true).order("numero"),
  ]);
  if (anterior.error) throw anterior.error;
  if (posteriores.error) throw posteriores.error;
  const comoRelacionado = (r: any, relacion: TicketRelacionado["relacion"]): TicketRelacionado => {
    const etapa = etapas.find((e) => e.id === String(r.id_etapa));
    return {
      id: String(r.id),
      numero: r.numero,
      nombre: r.nombre ?? "",
      pipelineId: String(r.id_pipeline),
      etapaId: String(r.id_etapa),
      etapa: etapa?.nombre ?? "sin etapa",
      terminal: esEtapaTerminal(etapa),
      relacion,
    };
  };
  return [
    ...(anterior.data ?? []).map((r: any) => comoRelacionado(r, "anterior")),
    ...(posteriores.data ?? []).map((r: any) => comoRelacionado(r, "posterior")),
  ];
}

/**
 * Folios relacionados de cada ticket (id -> folios), en las dos direcciones, para los chips de la
 * tarjeta y del detalle. Una sola lectura: las ligas son pocas. Solo activos y lo que deja la RLS.
 */
export async function fetchMapaRelacionados(): Promise<Map<string, number[]>> {
  const mapa = new Map<string, number[]>();
  if (!(await existeColumnaTicketRelacionado())) return mapa;
  const { data: ligas, error } = await sb
    .from("tickets")
    .select("id, numero, id_ticket_relacionado")
    .not("id_ticket_relacionado", "is", null)
    .eq("activo", true);
  if (error) throw error;
  if (!ligas?.length) return mapa;
  const ids = Array.from(new Set(ligas.map((l: any) => Number(l.id_ticket_relacionado))));
  const { data: anteriores, error: e2 } = await sb.from("tickets").select("id, numero").in("id", ids).eq("activo", true);
  if (e2) throw e2;
  const folio = new Map<string, number>((anteriores ?? []).map((t: any) => [String(t.id), t.numero]));
  const agrega = (id: string, numero: number) => {
    const lista = mapa.get(id) ?? [];
    if (!lista.includes(numero)) mapa.set(id, [...lista, numero].sort((a, b) => a - b));
  };
  for (const l of ligas as any[]) {
    const anterior = String(l.id_ticket_relacionado);
    const numAnterior = folio.get(anterior);
    if (numAnterior == null) continue;
    agrega(String(l.id), numAnterior);
    agrega(anterior, l.numero);
  }
  return mapa;
}

/** Folios relacionados por ticket. Se refresca con `["tickets-relacionados"]`, igual que el detalle. */
export function useMapaRelacionados(): Map<string, number[]> {
  const { data } = useQuery({
    queryKey: ["tickets-relacionados", "mapa"],
    queryFn: fetchMapaRelacionados,
    staleTime: 60_000,
  });
  return data ?? MAPA_VACIO;
}
const MAPA_VACIO = new Map<string, number[]>();

// DDL probe de tickets_actividad.editado_en (edición de mensajes, 20261018020000). Sin la
// columna la lista se lee igual y nadie aparece como "Editado".
let columnaEditado: Promise<boolean> | null = null;
function existeColumnaEditado(): Promise<boolean> {
  if (!columnaEditado) {
    columnaEditado = sb
      .from("tickets_actividad")
      .select("editado_en")
      .limit(0)
      .then(({ error }: { error: unknown }) => !error, () => false);
  }
  return columnaEditado as Promise<boolean>;
}

// DDL probe de tickets.fecha_etapa (cuándo entró a su etapa actual). Sin la columna el vencido
// se sigue contando desde la creación.
let columnaFechaEtapa: Promise<boolean> | null = null;
function existeColumnaFechaEtapa(): Promise<boolean> {
  if (!columnaFechaEtapa) {
    columnaFechaEtapa = sb
      .from("tickets")
      .select("fecha_etapa")
      .limit(0)
      .then(({ error }: { error: unknown }) => !error, () => false);
  }
  return columnaFechaEtapa as Promise<boolean>;
}

// DDL probe de tickets.fecha_vencimiento / fecha_inicio_atencion (vencimiento en horas hábiles,
// calculado en BD). Sin ellas el vencido se sigue calculando por días naturales en el front.
let columnaVencimiento: Promise<boolean> | null = null;
function existeColumnaVencimiento(): Promise<boolean> {
  if (!columnaVencimiento) {
    columnaVencimiento = sb
      .from("tickets")
      .select("fecha_vencimiento, fecha_inicio_atencion")
      .limit(0)
      .then(({ error }: { error: unknown }) => !error, () => false);
  }
  return columnaVencimiento as Promise<boolean>;
}

/** Select de la lista de tickets según las columnas opcionales que ya existen en el ambiente. */
async function selectTicketsVigente(): Promise<string> {
  const [conEditado, conFechaEtapa, conVencimiento] = await Promise.all([
    existeColumnaEditado(),
    existeColumnaFechaEtapa(),
    existeColumnaVencimiento(),
  ]);
  return selectTickets(conEditado, conFechaEtapa, conVencimiento);
}

/** Sin repetidos por id, conservando el orden (la primera es la principal). */
/** Llaves de «Guardar cambios» del detalle: solo las que cambiaron (ver `actualizar_ticket_completo`). */
export type CambiosTicketCompleto = {
  pipelineId?: string;
  etapaId?: string;
  categoriaId?: string;
  fuente?: string;
  prioridad?: Ticket["prioridad"];
  propietarios?: string[];
  solicitantes?: ContactoRef[];
  propiedades?: PropiedadRef[];
  /** Nota de actividad de las propiedades agregadas / quitadas. */
  notaPropiedades?: string;
  descripcion?: string;
  inmueble?: string;
};

/** `codigo|texto` de la RPC -> el texto; el resto de errores (triggers, RLS) tal cual. */
function textoErrorRpc(error: { message?: string } | null | undefined): string {
  const m = (error?.message ?? "").trim();
  const i = m.indexOf("|");
  return i > 0 && /^[a-z_]+$/.test(m.slice(0, i)) ? m.slice(i + 1).trim() : m;
}

function dedupePropiedades(lista: PropiedadRef[]): PropiedadRef[] {
  const vistos = new Set<string>();
  return lista.filter((p) => p && p.id && !vistos.has(p.id) && (vistos.add(p.id), true));
}

const selectTickets = (conEditado: boolean, conFechaEtapa = false, conVencimiento = false) =>
  "id, numero, nombre, id_pipeline, id_etapa, prioridad, id_categoria, id_usuario_creador, id_entidad_relacionada, id_propiedad, solicitante, inmueble, descripcion, fuente, fecha_creacion, fecha_cierre, " +
  (conFechaEtapa ? "fecha_etapa, " : "") +
  (conVencimiento ? "fecha_vencimiento, fecha_inicio_atencion, " : "") +
  "tickets_propietarios(id_usuario), tickets_actividad(id, tipo, texto, id_usuario_autor, id_etapa_destino, fecha_creacion, audio_url, audio_nombre, audio_mime, visible_cliente" +
  (conEditado ? ", editado_en)" : ")");

/**
 * Ids de «mis tickets» de un portal según el servidor; null = la RPC aún no existe.
 * `vistaUid` distinto de `actorUid` = "Ver como": se manda `p_auth_user_id` y el servidor exige
 * permiso de impersonar (20261019113742). Sin impersonar no se manda: misma llamada de siempre.
 */
async function idsTicketsPropios(
  menuId: number,
  vistaUid?: string | null,
  actorUid?: string | null,
): Promise<number[] | null> {
  const params: Record<string, unknown> = { p_menu_id: menuId };
  if (vistaUid && vistaUid !== actorUid) params.p_auth_user_id = vistaUid;
  const { data, error } = await sb.rpc("get_tickets_portal_propios", params);
  if (error) {
    if (error.code === "PGRST202" || error.code === "42883") return null;
    throw error;
  }
  return Array.from(new Set<number>((data ?? []).map((v: unknown) => Number(v)).filter(Number.isFinite)));
}

/**
 * "Ver como" fiel (20261022094554): los tickets que ve `vistaUid` según SU regla de fila, en la
 * misma forma que el select de la lista. Las tablas se leen con el token del admin, que puede ver
 * más o menos que el impersonado; `get_tickets_vista_como` evalúa la regla con la identidad del
 * impersonado (el servidor exige puede_impersonar). null = la RPC aún no existe.
 */
async function ticketsVistaComo(vistaUid: string, ids?: number[]): Promise<any[] | null> {
  const params: Record<string, unknown> = { p_auth_user_id: vistaUid };
  if (ids) params.p_ids = ids;
  const { data, error } = await sb.rpc("get_tickets_vista_como", params);
  if (error) {
    if (error.code === "PGRST202" || error.code === "42883") return null;
    throw error;
  }
  return Array.isArray(data) ? data : [];
}

/**
 * Filas de la lista en "Ver como", con el mismo universo que arma `fetchTickets` para la sesión:
 * «solo los míos» = los ids de la RPC de propios; portal con áreas destino = esos pipelines ∪
 * levantados ∪ asignados; sin alcance = todo lo que ve el impersonado. null = sin la RPC.
 */
async function filasVistaComo(
  vistaUid: string,
  idsPropios: number[] | null,
  propiosMenuId?: number,
  pipelineIds?: number[],
): Promise<any[] | null> {
  if (propiosMenuId != null) {
    if (idsPropios === null) return null;
    return idsPropios.length ? ticketsVistaComo(vistaUid, idsPropios) : [];
  }
  const filas = await ticketsVistaComo(vistaUid);
  if (!filas || !pipelineIds) return filas;
  const alcance = new Set(pipelineIds);
  return filas.filter(
    (r: any) =>
      alcance.has(Number(r.id_pipeline)) ||
      r.id_usuario_creador === vistaUid ||
      (r.tickets_propietarios ?? []).some((p: any) => p.id_usuario === vistaUid),
  );
}

/**
 * Inicio (hora de Ciudad de México) del mes en curso menos `meses`, en ISO: el corte de la ventana
 * de tickets cerrados.
 */
export function inicioMesesAtras(meses: number, hoy: Date = new Date()): string {
  const [anio, mes] = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Mexico_City", year: "numeric", month: "2-digit" })
    .format(hoy)
    .split("-")
    .map(Number);
  const d = new Date(Date.UTC(anio, mes - 1 - meses, 1));
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  // CDMX es UTC-6 todo el año (sin horario de verano desde 2022).
  return `${d.getUTCFullYear()}-${mm}-01T00:00:00-06:00`;
}

/** ¿Hay tickets cerrados antes del corte? (conteo sin bajar filas; la RLS acota al usuario). */
async function hayCerradosAntes(corte: string): Promise<boolean> {
  const { count, error } = await sb
    .from("tickets")
    .select("id", { count: "exact", head: true })
    .eq("activo", true)
    .lt("fecha_cierre", corte);
  if (error) return false;
  return (count ?? 0) > 0;
}

/** Tickets por id en lotes (la lista viaja en la URL), más nuevos primero. */
async function ticketsPorIds(ids: number[]): Promise<any[]> {
  const SELECT_TICKETS = await selectTicketsVigente();
  const LOTE = 200;
  const lotes: number[][] = [];
  for (let i = 0; i < ids.length; i += LOTE) lotes.push(ids.slice(i, i + LOTE));
  const res = await Promise.all(
    lotes.map((l) => sb.from("tickets").select(SELECT_TICKETS).eq("activo", true).in("id", l)),
  );
  const fallo = res.find((r: any) => r.error);
  if (fallo) throw fallo.error;
  return res
    .flatMap((r: any) => r.data ?? [])
    .sort((a: any, b: any) => new Date(b.fecha_creacion).getTime() - new Date(a.fecha_creacion).getTime());
}

/**
 * Sin `pipelineIds` = todos los tickets activos (Portal Tickets, Alta Dirección). Con
 * `pipelineIds` el filtro va al servidor: tickets de esos pipelines ∪ levantados por `uid` ∪
 * asignados a `uid`. Así un portal con dos áreas destino no baja la mesa completa.
 * Con `propiosMenuId` (Tickets de Cobranza/Productos) solo levantados ∪ asignados, vía RPC.
 * `uid` es el usuario cuya vista se arma (el impersonado en "Ver como"); `actorUid` el de la
 * sesión. Solo filtran: lo que se escribe (autor, creador, adjuntos) usa siempre la sesión.
 */
async function fetchTickets(
  pipelineIds?: number[],
  uid?: string | null,
  propiosMenuId?: number,
  pipelinesVisibles?: number[],
  actorUid?: string | null,
  cerradosDesde?: string | null,
  soloPipelinesVisibles = false,
): Promise<Ticket[]> {
  let rows: any[];
  // Página «solo los míos» de un portal: el servidor dice cuáles son (levantados ∪ asignados).
  const idsPropios = propiosMenuId != null ? await idsTicketsPropios(propiosMenuId, uid, actorUid) : null;
  // "Ver como": las filas salen de la regla del impersonado, no de lo que el admin lee directo.
  const vistaRows = uid && actorUid && uid !== actorUid ? await filasVistaComo(uid, idsPropios, propiosMenuId, pipelineIds) : null;
  if (vistaRows !== null) {
    rows = vistaRows;
  } else if (idsPropios !== null) {
    rows = idsPropios.length ? await ticketsPorIds(idsPropios) : [];
  } else {
    // Sin la RPC (migración pendiente) la página «solo los míos» arma el mismo universo aquí:
    // sin pipelines del portal, solo levantados por `uid` ∪ asignados a `uid`.
    const alcancePipelines = propiosMenuId != null ? [] : pipelineIds;
    const select = await selectTicketsVigente();
    let filtroAlcance: string | null = null;
    if (alcancePipelines) {
      const condiciones: string[] = [];
      if (alcancePipelines.length) condiciones.push(`id_pipeline.in.(${alcancePipelines.join(",")})`);
      if (uid) {
        condiciones.push(`id_usuario_creador.eq.${uid}`);
        const { data: mios, error: miosErr } = await sb
          .from("tickets_propietarios")
          .select("id_ticket")
          .eq("id_usuario", uid);
        if (miosErr) throw miosErr;
        const ids = Array.from(new Set((mios ?? []).map((m: any) => Number(m.id_ticket))));
        if (ids.length) condiciones.push(`id.in.(${ids.join(",")})`);
      }
      if (!condiciones.length) return [];
      filtroAlcance = condiciones.join(",");
    }
    // Consulta con el alcance ya puesto; se arma de nuevo para cada mitad de la ventana.
    // Síncrona a propósito: el builder es "thenable" y un async lo ejecutaría al devolverlo.
    const consultaBase = () => {
      const base = sb.from("tickets").select(select).eq("activo", true);
      return filtroAlcance ? base.or(filtroAlcance) : base;
    };
    const q = consultaBase();
    if (cerradosDesde) {
      // Abiertos (sin fecha de cierre) completos + cerrados desde el corte: dos consultas con el
      // mismo alcance, porque el alcance ya usa el único `or` del filtro.
      const [abiertos, cerrados] = await Promise.all([
        q.is("fecha_cierre", null).order("fecha_creacion", { ascending: false }),
        consultaBase().gte("fecha_cierre", cerradosDesde).order("fecha_creacion", { ascending: false }),
      ]);
      if (abiertos.error) throw abiertos.error;
      if (cerrados.error) throw cerrados.error;
      rows = [...(abiertos.data ?? []), ...(cerrados.data ?? [])];
    } else {
      const { data, error } = await q.order("fecha_creacion", { ascending: false });
      if (error) throw error;
      rows = data ?? [];
    }
  }
  // Las RPC de "ver como" y «solo los míos» devuelven su universo completo: aquí se aplica la
  // misma ventana de cerrados.
  if (cerradosDesde) {
    const corte = new Date(cerradosDesde).getTime();
    rows = rows.filter((r: any) => !r.fecha_cierre || new Date(r.fecha_cierre).getTime() >= corte);
  }
  // La RPC ya filtra por destinos del portal; esto cubre el camino sin RPC. En el Portal Condominio
  // deja fuera lo levantado o asignado en otras áreas.
  if ((propiosMenuId != null || soloPipelinesVisibles) && pipelinesVisibles) {
    const visibles = new Set(pipelinesVisibles);
    rows = rows.filter((r: any) => visibles.has(Number(r.id_pipeline)));
  }

  // Resolver nombres de autores de actividad + del creador del ticket (uuid → nombre) en un solo fetch.
  const uuids = new Set<string>();
  for (const r of rows) {
    for (const a of r.tickets_actividad ?? []) if (a.id_usuario_autor) uuids.add(a.id_usuario_autor);
    if (r.id_usuario_creador) uuids.add(r.id_usuario_creador);
  }
  let nameMap: Record<string, string> = {};
  if (uuids.size) {
    const { data: us } = await sb
      .from("usuarios")
      .select("auth_user_id, nombre")
      .in("auth_user_id", Array.from(uuids));
    nameMap = Object.fromEntries((us ?? []).map((u: any) => [u.auth_user_id, u.nombre]));
  }

  // Solicitantes (multi): la tabla de unión tickets_solicitantes puede no existir aún en dev
  // (migración pendiente) → se consulta aparte con fallback. Si un ticket no tiene filas en la
  // unión, se usa el solicitante legacy (tickets.id_entidad_relacionada) para no perder el dato.
  const ticketIds = rows.map((r: any) => r.id);
  const solByTicket: Record<string, number[]> = {};
  if (ticketIds.length) {
    const { data: solRows, error: solErr } = await sb
      .from("tickets_solicitantes")
      .select("id_ticket, id_entidad_relacionada")
      .in("id_ticket", ticketIds);
    if (!solErr) {
      for (const s of solRows ?? []) (solByTicket[String(s.id_ticket)] ||= []).push(s.id_entidad_relacionada);
    }
  }

  // Propiedades ligadas (tickets_propiedades). null = la tabla aún no está en el ambiente: cada
  // ticket cae a su id_propiedad + inmueble de siempre y la carga no se rompe.
  const propsByTicket = ticketIds.length ? await fetchPropiedadesDeTickets(ticketIds) : {};

  // Palomitas de la Conversación (migraciones 20261016235500 y 20261019153742). Consulta aparte y
  // tolerante: si la tabla aún no está en el ambiente, la lista se sirve igual y las palomitas
  // quedan en gris. `*` y no la lista de columnas: sin la columna de "recibido" (ambiente sin la
  // segunda migración) pedirla por nombre da 42703 y se perderían también las de leído.
  const lecturaByTicket: Record<string, { contraparte: string | null; equipo: string | null; recibido: string | null }> = {};
  if (ticketIds.length) {
    const { data: lecRows, error: lecErr } = await sb
      .from("tickets_lecturas")
      .select("*")
      .in("id_ticket", ticketIds);
    if (!lecErr) {
      for (const l of lecRows ?? [])
        lecturaByTicket[String(l.id_ticket)] = {
          contraparte: l.contraparte_leido_hasta ?? null,
          equipo: l.equipo_leido_hasta ?? null,
          recibido: l.contraparte_recibido_hasta ?? null,
        };
    }
  }
  const entIdsByTicket: Record<string, number[]> = {};
  const entIds = new Set<number>();
  for (const r of rows) {
    const fromJoin = solByTicket[String(r.id)];
    const list =
      fromJoin && fromJoin.length
        ? fromJoin
        : r.id_entidad_relacionada != null
          ? [r.id_entidad_relacionada]
          : [];
    entIdsByTicket[String(r.id)] = list;
    for (const id of list) entIds.add(id);
  }
  let contactoMap: Record<string, ContactoRef> = {};
  if (entIds.size) {
    const { data: ents } = await sb
      .from("entidades_relacionadas")
      .select("id, id_persona")
      .in("id", Array.from(entIds));
    const personaIds = Array.from(new Set((ents ?? []).map((e: any) => e.id_persona).filter(Boolean)));
    let personaMap: Record<string, { nombre: string; email: string; telefono: string | null }> = {};
    if (personaIds.length) {
      const { data: personas } = await sb
        .from("personas")
        .select("id, nombre_legal, nombre_comercial, email, telefono, clave_pais_telefono")
        .in("id", personaIds);
      personaMap = Object.fromEntries(
        (personas ?? []).map((p: any) => [
          String(p.id),
          {
            nombre: (p.nombre_legal || p.nombre_comercial || "Sin nombre").trim(),
            email: p.email ?? "",
            telefono: telefonoDisplay(p),
          },
        ]),
      );
    }
    contactoMap = Object.fromEntries(
      (ents ?? []).map((e: any): [string, ContactoRef] => {
        const p = personaMap[String(e.id_persona)];
        return [
          String(e.id),
          { id: String(e.id), nombre: p?.nombre ?? "Contacto", email: p?.email ?? "", telefono: p?.telefono ?? null },
        ];
      }),
    );
  }

  return rows.map((r: any) => ({
    id: String(r.id),
    numero: r.numero,
    nombre: r.nombre,
    pipelineId: String(r.id_pipeline),
    etapaId: String(r.id_etapa),
    prioridad: r.prioridad,
    categoriaId: r.id_categoria != null ? String(r.id_categoria) : "",
    propietarios: (r.tickets_propietarios ?? []).map((p: any) => p.id_usuario),
    creadoPorId: r.id_usuario_creador ?? null,
    creadoPor: r.id_usuario_creador ? nameMap[r.id_usuario_creador] ?? null : null,
    solicitante: r.solicitante ?? "",
    solicitantes: (entIdsByTicket[String(r.id)] ?? [])
      .map((eid) => contactoMap[String(eid)])
      .filter(Boolean) as ContactoRef[],
    inmueble: r.inmueble ?? "",
    descripcion: r.descripcion ?? "",
    fechaCreacion: r.fecha_creacion,
    fechaEtapa: r.fecha_etapa ?? null,
    fechaVencimiento: r.fecha_vencimiento ?? null,
    fechaInicioAtencion: r.fecha_inicio_atencion ?? null,
    fechaCierre: r.fecha_cierre ?? null,
    // Clave del catálogo; claveFuente traduce texto viejo si alguna fila no se migró.
    fuente: claveFuente(r.fuente),
    entidadRelacionadaId: r.id_entidad_relacionada != null ? String(r.id_entidad_relacionada) : null,
    contraparteLeidoHasta: lecturaByTicket[String(r.id)]?.contraparte ?? null,
    equipoLeidoHasta: lecturaByTicket[String(r.id)]?.equipo ?? null,
    contraparteRecibidoHasta: lecturaByTicket[String(r.id)]?.recibido ?? null,
    propiedadId: r.id_propiedad != null ? String(r.id_propiedad) : null,
    propiedades: propiedadesDeFila(r, propsByTicket),
    actividad: (r.tickets_actividad ?? [])
      .map((a: any) => ({
        id: String(a.id),
        fecha: a.fecha_creacion,
        // Sin autor + "comentario" = lo escribió el cliente desde su app (mismo criterio que
        // Atención a Clientes de cobranza); antes se pintaba como "Sistema".
        autor: a.id_usuario_autor
          ? nameMap[a.id_usuario_autor] ?? "Usuario"
          : a.tipo === "comentario"
            ? "Cliente"
            : "Sistema",
        autorId: a.id_usuario_autor ?? null,
        texto: a.texto,
        etapaDestinoId: a.id_etapa_destino != null ? String(a.id_etapa_destino) : null,
        audioUrl: a.audio_url ?? null,
        tipo: a.tipo ?? null,
        visibleCliente: a.visible_cliente === true,
        editadoEn: a.editado_en ?? null,
      }))
      .sort((x: any, y: any) => new Date(x.fecha).getTime() - new Date(y.fecha).getTime()),
  }));
}

/**
 * Propiedades de un ticket para el front. Con la tabla de unión: sus filas, con la principal
 * (tickets.id_propiedad) primero. Sin tabla o sin filas: la principal sola, con `inmueble` de
 * etiqueta (así lo llena la app de clientes: "Proyecto - U-101").
 */
function propiedadesDeFila(r: any, propsByTicket: Record<string, PropiedadRef[]> | null): PropiedadRef[] {
  const principal = r.id_propiedad != null ? String(r.id_propiedad) : null;
  const deTabla = propsByTicket?.[String(r.id)] ?? [];
  if (deTabla.length) {
    const i = principal ? deTabla.findIndex((p) => p.id === principal) : -1;
    return i > 0 ? [deTabla[i], ...deTabla.slice(0, i), ...deTabla.slice(i + 1)] : deTabla;
  }
  if (!principal) return [];
  return [
    {
      id: principal,
      etiqueta: (r.inmueble ?? "").trim() || `Propiedad ${principal}`,
      idCuentaCobranza: r.id_cuenta_cobranza != null ? String(r.id_cuenta_cobranza) : null,
      idProyecto: r.id_proyecto != null ? String(r.id_proyecto) : null,
    },
  ];
}

/**
 * Primer usuario activo vinculado a la categoría (orden de configuración). Los roles de la
 * categoría NO cuentan: expanden a todo el rol y sirven para notificar, no para ser dueño.
 */
export async function primerResponsableCategoria(idCategoria: string | number): Promise<string | null> {
  const { data: ligas, error } = await sb
    .from("tickets_categorias_usuarios")
    .select("id_usuario")
    .eq("id_categoria", Number(idCategoria))
    .order("id");
  if (error) return null;
  const ids = (ligas ?? []).map((l: any) => l.id_usuario).filter(Boolean) as string[];
  if (!ids.length) return null;
  const { data: us } = await sb.from("usuarios").select("auth_user_id").in("auth_user_id", ids).eq("activo", true);
  const activos = new Set((us ?? []).map((u: any) => u.auth_user_id));
  return ids.find((id) => activos.has(id)) ?? null;
}

export function TicketsProvider({
  children,
  pipelineIds,
  fuente,
  rutasAgentes,
  categoriaPorPipeline,
  cargarTickets = true,
  propiosMenuId,
  pipelinesVisibles,
  soloPipelinesVisibles,
  asignablesProyectoId,
  vistaUid,
}: {
  children: ReactNode;
  autor?: string;
  /**
   * Usuario cuya lista se ve (`usuarios.auth_user_id`), p. ej. el impersonado en "Ver como".
   * Solo filtra la lista; quien escribe sigue siendo la sesión (`uid`). Sin la prop = la sesión;
   * `null` explícito = no hay a quién mostrar (impersonado sin acceso) y la lista queda vacía.
   */
  vistaUid?: string | null;
} & AlcanceTickets) {
  const { user, profile } = useAuth();
  const logger = useActivityLogger();
  const qc = useQueryClient();
  // uid = actor (autor, creador, adjuntos, notificaciones). vista = a quién pertenece la lista.
  const uid: string | null = user?.id ?? null;
  const vista: string | null = vistaUid !== undefined ? vistaUid : uid;
  // "Ver como" (la lista es de otro usuario): todo en solo lectura. Las escrituras de abajo se
  // sustituyen por un aviso; las acciones siempre serían con los permisos reales de la sesión.
  const verComo = vistaUid !== undefined && vista !== uid;
  const autor = (profile as any)?.nombre || user?.email || "Sistema";

  const pipelinesQ = useQuery({ queryKey: ["tickets-pipelines"], queryFn: fetchPipelines });
  const etapasQ = useQuery({ queryKey: ["tickets-etapas"], queryFn: fetchEtapas });
  const categoriasQ = useQuery({ queryKey: ["tickets-categorias"], queryFn: fetchCategorias });
  // Catálogo casi estático: se pide una vez por sesión.
  const fuentesQ = useQuery({
    queryKey: ["tickets-fuentes"],
    queryFn: fetchFuentes,
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });
  // Las claves sin alcance son las de siempre: el Portal Tickets comparte caché como antes.
  const rutasKey = (rutasAgentes ?? []).join("|");
  const agentesQ = useQuery({
    queryKey:
      asignablesProyectoId != null
        ? ["tickets-agentes", "condominio", asignablesProyectoId]
        : rutasKey
          ? ["tickets-agentes", rutasKey]
          : ["tickets-agentes"],
    queryFn: () =>
      asignablesProyectoId != null
        ? fetchAgentesCondominio(asignablesProyectoId, rutasAgentes ?? [])
        : fetchAgentesConRutas(rutasAgentes ?? []),
  });
  const pipelinesKey = pipelineIds ? [...pipelineIds].sort((a, b) => a - b).join(",") : null;
  // Ventana de cerrados: mes en curso (1) y un mes más por cada «Ver más»; 0 = todos.
  const [mesesCerrados, setMesesCerrados] = useState(1);
  const [todosCerrados, setTodosCerrados] = useState(false);
  const cerradosDesde = todosCerrados ? null : inicioMesesAtras(mesesCerrados - 1);
  const masCerradosQ = useQuery({
    queryKey: ["tickets-list", "hay-mas-cerrados", cerradosDesde],
    queryFn: () => hayCerradosAntes(cerradosDesde as string),
    enabled: cargarTickets && !!cerradosDesde,
    staleTime: 5 * 60_000,
  });
  const ticketsQ = useQuery({
    // La ventana va al final de la llave: invalidar ["tickets-list"] sigue alcanzando a todas.
    queryKey: [
      ...(propiosMenuId != null
        ? ["tickets-list", "propios", propiosMenuId, vista, (pipelinesVisibles ?? []).join(",")]
        : pipelinesKey === null
          ? verComo
            ? ["tickets-list", "vista", vista]
            : ["tickets-list"]
          : [
              "tickets-list",
              "alcance",
              pipelinesKey,
              vista,
              ...(soloPipelinesVisibles ? ["solo", (pipelinesVisibles ?? []).join(",")] : []),
            ]),
      cerradosDesde ?? "todos",
    ],
    queryFn: () =>
      fetchTickets(pipelineIds, vista, propiosMenuId, pipelinesVisibles, uid, cerradosDesde, soloPipelinesVisibles),
    enabled: cargarTickets && ((pipelinesKey === null && propiosMenuId == null) || (!!uid && !!vista)),
    // Al cambiar de vista o de alcance se quedan los tickets anteriores (atenuados) hasta que
    // llegan los nuevos: el "Cargando…" a pantalla completa es solo para la primera carga.
    placeholderData: keepPreviousData,
  });
  const categoriasKey = categoriaPorPipeline ? JSON.stringify(categoriaPorPipeline) : "";
  const alcance = useMemo<AlcanceTickets>(
    () => ({
      pipelineIds, fuente, rutasAgentes, categoriaPorPipeline, propiosMenuId, pipelinesVisibles,
      soloPipelinesVisibles, asignablesProyectoId,
    }),
    // Claves por valor: los arrays/objetos llegan nuevos en cada render de la página.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pipelinesKey, fuente, rutasKey, categoriasKey, propiosMenuId, (pipelinesVisibles ?? []).join(","), soloPipelinesVisibles, asignablesProyectoId],
  );

  const pipelines = pipelinesQ.data ?? [];
  const etapas = etapasQ.data ?? [];
  const categorias = categoriasQ.data ?? [];
  const fuentes = fuentesQ.data ?? FUENTES_RESPALDO;
  const etiquetaFuente = useCallback((clave: string | null | undefined) => etiquetaDeFuente(clave, fuentes), [fuentes]);
  const agentes = agentesQ.data ?? [];
  const tickets = ticketsQ.data ?? [];

  const invalidate = useCallback(
    (key: string) => qc.invalidateQueries({ queryKey: [key] }),
    [qc],
  );

  // Tiempo real: Realtime solo AVISA que algo cambió y TanStack recarga la lista. Así no se
  // arma estado a mano con los payloads y basta un refetch por ráfaga de cambios (debounce):
  // un ticket nuevo con 3 adjuntos son ~6 eventos y una sola recarga.
  const canalId = useId();
  useEffect(() => {
    let espera: ReturnType<typeof setTimeout> | undefined;
    const recargar = () => {
      clearTimeout(espera);
      espera = setTimeout(() => qc.invalidateQueries({ queryKey: ["tickets-list"] }), 800);
    };
    let canal = supabase.channel(`tickets-lista-${canalId.replace(/:/g, "")}`);
    for (const table of TABLAS_TIEMPO_REAL) {
      canal = canal.on("postgres_changes", { event: "*", schema: "public", table }, recargar);
    }
    canal.subscribe();
    // Palomitas de leído: canal aparte para que, si en algún ambiente la tabla aún no está
    // publicada, no tumbe la suscripción de la lista.
    const canalLecturas = supabase
      .channel(`tickets-lecturas-${canalId.replace(/:/g, "")}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "tickets_lecturas" }, recargar)
      .subscribe();
    return () => {
      clearTimeout(espera);
      supabase.removeChannel(canal);
      supabase.removeChannel(canalLecturas);
    };
  }, [qc, canalId]);

  // `visibleCliente` es lo que separa una nota interna de una RESPUESTA. El
  // default es false: lo que se escribe aqui es del equipo salvo que se diga
  // lo contrario, que es como se ha trabajado siempre.
  //
  // WARN: en true dispara el push al cliente (trigger
  // trg_tickets_respuesta_push_cliente). No es solo cambiar quien lo ve.
  const registrarActividad = useCallback(
    async (
      idTicket: string,
      texto: string,
      tipo = "sistema",
      audio?: { url: string; nombre: string | null; mime: string | null } | null,
      visibleCliente = false,
      // Cambio de etapa: se guardan los ids para que la Analítica no dependa del texto.
      etapa?: { origen: string | null; destino: string } | null,
    ): Promise<boolean> => {
      // Antes el error del insert se tiraba: la UI decía "Nota agregada" aunque RLS la rechazara.
      const { error } = await sb.from("tickets_actividad").insert({
        id_ticket: Number(idTicket),
        texto,
        tipo,
        id_usuario_autor: uid,
        audio_url: audio?.url ?? null,
        audio_nombre: audio?.nombre ?? null,
        audio_mime: audio?.mime ?? null,
        visible_cliente: visibleCliente,
        id_etapa_origen: etapa?.origen ? Number(etapa.origen) : null,
        id_etapa_destino: etapa ? Number(etapa.destino) : null,
      });
      if (error) {
        toast.error(`No se pudo registrar en la actividad: ${error.message}`);
        return false;
      }
      return true;
    },
    [uid],
  );

  // Nombre del pipeline (para el cuerpo de los correos).
  const pipelineNombre = useCallback(
    (pipelineId?: string | null) => pipelines.find((p) => p.id === pipelineId)?.nombre ?? "",
    [pipelines],
  );

  // Resuelve el email de cada propietario NUEVO (por auth_user_id) y dispara el correo de
  // asignación. `autor` = quién está asignando.
  const notificarAsignacion = useCallback(
    (idsNuevos: string[], info: Omit<CorreoTicketInfo, "por">) => {
      const destinatarios = idsNuevos
        .map((id) => agentes.find((a) => a.id === id))
        .filter((a): a is Agente => !!a?.email);
      enviarCorreoAsignacion(destinatarios, { ...info, por: autor });
    },
    [agentes, autor],
  );

  // Al cerrar un ticket (etapa final) avisa por correo a los propietarios + el creador.
  const notificarCierre = useCallback(
    (tk: Ticket) => {
      const ids = Array.from(
        new Set([...(tk.propietarios ?? []), tk.creadoPorId].filter(Boolean) as string[]),
      );
      const destinatarios = ids
        .map((id) => agentes.find((a) => a.id === id))
        .filter((a): a is Agente => !!a?.email);
      enviarCorreoCierre(destinatarios, {
        folio: tk.numero,
        nombre: tk.nombre,
        pipeline: pipelineNombre(tk.pipelineId),
        proyecto: tk.inmueble,
        descripcion: tk.descripcion,
        por: autor,
      });
    },
    [agentes, autor, pipelineNombre],
  );

  // Al agregar una nota avisa SOLO por correo a propietarios + creador, MENOS el
  // autor de la nota (uid). Solo correo: las notas son frecuentes → evita ruido en WhatsApp.
  const notificarNota = useCallback(
    (tk: Ticket, nota: string) => {
      const ids = Array.from(
        new Set([...(tk.propietarios ?? []), tk.creadoPorId].filter(Boolean) as string[]),
      ).filter((id) => id !== uid);
      const destinatarios = ids
        .map((id) => agentes.find((a) => a.id === id))
        .filter((a): a is Agente => !!a?.email)
        .map((a) => ({ email: a.email, nombre: a.nombre })); // sin teléfono → solo correo
      if (destinatarios.length === 0) return;
      enviarCorreoNota(
        destinatarios,
        {
          folio: tk.numero,
          nombre: tk.nombre,
          pipeline: pipelineNombre(tk.pipelineId),
          proyecto: tk.inmueble,
          descripcion: tk.descripcion,
          por: autor,
        },
        nota,
      );
    },
    [agentes, autor, uid, pipelineNombre],
  );

  const crearTicket = useCallback(
    async (data: NuevoTicket): Promise<TicketCreado | null> => {
      const sols = (data.solicitantes ?? []).filter(Boolean);
      const principal = sols[0] ?? null;
      // Sin propietario elegido y con categoría: dueño = primera persona vinculada a la
      // categoría (tickets_categorias_usuarios, en orden), igual que Atención a Clientes.
      // Solo en portales con alcance: en el Portal Tickets «Sin asignar» es una decisión.
      let propietariosFinales = (data.propietarios ?? []).filter(Boolean);
      if (!propietariosFinales.length && data.categoriaId && alcance.pipelineIds) {
        const dueno = await primerResponsableCategoria(data.categoriaId);
        if (dueno) propietariosFinales = [dueno];
      }
      // Propiedades: la primera es la principal y llena las columnas de siempre si no vienen.
      const propsTicket = dedupePropiedades(data.propiedades ?? []);
      const propPrincipal = propsTicket[0] ?? null;
      const propiedadId = data.propiedadId || propPrincipal?.id || null;
      const proyectoId = data.proyectoId || propPrincipal?.idProyecto || null;
      const cuentaCobranzaId = data.cuentaCobranzaId || propPrincipal?.idCuentaCobranza || null;
      const inmueble = data.inmueble?.trim() || propPrincipal?.etiqueta || "";
      const extra: Record<string, unknown> = {};
      if (proyectoId) extra.id_proyecto = Number(proyectoId);
      if (cuentaCobranzaId && (await existeColumnaCuentaCobranza()))
        extra.id_cuenta_cobranza = Number(cuentaCobranzaId);
      if (data.ticketRelacionadoId) {
        if (!(await existeColumnaTicketRelacionado())) {
          toast.error("Relacionar con un ticket anterior aún no está disponible. Quita el folio para crear el ticket.");
          return null;
        }
        extra.id_ticket_relacionado = Number(data.ticketRelacionadoId);
      }
      const { data: ins, error } = await sb
        .from("tickets")
        .insert({
          ...extra,
          nombre: data.nombre.trim(),
          id_pipeline: Number(data.pipelineId),
          id_etapa: Number(data.etapaId),
          prioridad: data.prioridad,
          id_categoria: data.categoriaId ? Number(data.categoriaId) : null,
          id_usuario_propietario: propietariosFinales[0] || null,
          id_usuario_creador: uid,
          id_entidad_relacionada: principal
            ? Number(principal.id)
            : data.entidadRelacionadaId
              ? Number(data.entidadRelacionadaId)
              : null,
          id_propiedad: propiedadId ? Number(propiedadId) : null,
          solicitante: (principal ? principal.nombre : data.solicitante)?.trim() || null,
          inmueble: inmueble || null,
          descripcion: data.descripcion?.trim() || null,
          fuente: claveFuente(data.fuente || alcance.fuente, fuentes),
          fecha_creacion: data.fechaCreacion ?? undefined,
        })
        .select("id, numero, fecha_creacion")
        .single();
      if (error) {
        // El trigger valida que el ticket relacionado exista y siga activo.
        toast.error(`No se pudo crear el ticket: ${mensajeErrorTicketRelacionado(error)}`);
        return null;
      }
      if (ins?.id) {
        if (sols.length) {
          await sb
            .from("tickets_solicitantes")
            .insert(sols.map((s: ContactoRef) => ({ id_ticket: ins.id, id_entidad_relacionada: Number(s.id) })));
        }
        if (propsTicket.length) {
          // La principal ya quedó en tickets.id_propiedad; sin la tabla, las demás se perderían.
          const r = await sincronizarPropiedadesTicket(ins.id, propsTicket);
          if (r === "sin_tabla" && propsTicket.length > 1)
            toast.warning("El ticket se creó solo con la primera propiedad: ligar varias aún no está disponible.");
          else if (typeof r === "object") toast.error(`No se pudieron ligar las propiedades: ${r.error}`);
        }
        const props = propietariosFinales;
        if (props.length) {
          await sb
            .from("tickets_propietarios")
            .insert(props.map((u: string) => ({ id_ticket: ins.id, id_usuario: u })));
          notificarAsignacion(props, {
            folio: ins.numero,
            nombre: data.nombre.trim(),
            pipeline: pipelineNombre(data.pipelineId),
            proyecto: inmueble,
            descripcion: data.descripcion,
          });
        }
        if (data.adjuntos?.length) {
          await saveTicketAdjuntos(ins.id, uid, data.adjuntos);
        }
        await registrarActividad(
          String(ins.id),
          alcance.fuente
            ? `Ticket creado desde ${etiquetaDeFuente(alcance.fuente, fuentes)}.`
            : "Ticket creado desde el Portal Tickets de Seguimiento.",
        );
        logger.registrarCreacion(
          "ticket",
          {
            id_ticket: ins.id,
            nombre: data.nombre.trim(),
            id_pipeline: Number(data.pipelineId),
            prioridad: data.prioridad,
          },
          "crear_ticket",
        );
      }
      invalidate("tickets-list");
      if (data.ticketRelacionadoId) invalidate("tickets-relacionados");
      return { id: String(ins.id), numero: ins.numero, fechaCreacion: ins.fecha_creacion };
    },
    [uid, alcance, fuentes, registrarActividad, invalidate, logger, notificarAsignacion, pipelineNombre],
  );

  const actualizarTicket = useCallback(
    async (id: string, cambios: Partial<Ticket>, nota?: string): Promise<boolean> => {
      const patch: Record<string, unknown> = {};
      // Un caso de Atención a Clientes que entra o sale de una bandeja (Cobranza / Productos) no
      // se mueve con un UPDATE: la BD lo bloquea (trg_tickets_candado_traspaso_atencion) y lo hace
      // `atencion_reclasificar_caso`, que valida permisos y deja su propio movimiento interno.
      let reclasificadoEnBd = false;
      if ("categoriaId" in cambios) {
        const tk = tickets.find((t) => t.id === id);
        const destino = cambios.categoriaId || "";
        const cruza = cruzaBandejaAtencion(
          pipelines.find((p) => p.id === tk?.pipelineId)?.codigo,
          categorias.find((c) => c.id === tk?.categoriaId)?.bandejaAtencion,
          categorias.find((c) => c.id === destino)?.bandejaAtencion,
        );
        if (cruza) {
          if (!destino) {
            toast.error("Para sacar el caso de su bandeja elige otra categoría.");
            return false;
          }
          const { error } = await sb.rpc("atencion_reclasificar_caso", {
            p_id_ticket: Number(id),
            p_id_categoria_destino: Number(destino),
          });
          if (error) {
            toast.error(mensajeErrorReclasificar(error));
            invalidate("tickets-list");
            return false;
          }
          reclasificadoEnBd = true;
        }
      }
      if ("prioridad" in cambios) patch.prioridad = cambios.prioridad;
      if ("propietarios" in cambios) {
        const props = (cambios.propietarios ?? []).filter(Boolean);
        const tk = tickets.find((t) => t.id === id);
        const previos = tk?.propietarios ?? [];
        const nuevos = props.filter((p) => !previos.includes(p));
        const { error: errBorrar } = await sb.from("tickets_propietarios").delete().eq("id_ticket", Number(id));
        const { error: errAlta } = props.length
          ? await sb
              .from("tickets_propietarios")
              .insert(props.map((u: string) => ({ id_ticket: Number(id), id_usuario: u })))
          : { error: null };
        if (errBorrar || errAlta) {
          toast.error(`No se pudieron guardar los responsables: ${(errBorrar ?? errAlta)!.message}`);
          invalidate("tickets-list");
          return false;
        }
        patch.id_usuario_propietario = props[0] || null;
        // Notificar SOLO a los propietarios recién agregados.
        if (nuevos.length)
          notificarAsignacion(nuevos, {
            folio: tk?.numero ?? "",
            nombre: tk?.nombre ?? "Ticket",
            pipeline: pipelineNombre(tk?.pipelineId),
            proyecto: tk?.inmueble,
            descripcion: tk?.descripcion,
          });
      }
      if ("solicitantes" in cambios) {
        const sols = (cambios.solicitantes ?? []).filter(Boolean);
        const { error: errBorrar } = await sb.from("tickets_solicitantes").delete().eq("id_ticket", Number(id));
        const { error: errAlta } = sols.length
          ? await sb
              .from("tickets_solicitantes")
              .insert(sols.map((s) => ({ id_ticket: Number(id), id_entidad_relacionada: Number(s.id) })))
          : { error: null };
        if (errBorrar || errAlta) {
          toast.error(`No se pudieron guardar los solicitantes: ${(errBorrar ?? errAlta)!.message}`);
          invalidate("tickets-list");
          return false;
        }
        // Mantener el "principal" en las columnas legacy (primer solicitante).
        patch.id_entidad_relacionada = sols[0] ? Number(sols[0].id) : null;
        patch.solicitante = sols[0]?.nombre?.trim() || null;
      }
      if ("categoriaId" in cambios && !reclasificadoEnBd)
        patch.id_categoria = cambios.categoriaId ? Number(cambios.categoriaId) : null;
      if ("solicitante" in cambios) patch.solicitante = cambios.solicitante || null;
      if ("inmueble" in cambios) patch.inmueble = cambios.inmueble || null;
      if ("descripcion" in cambios) patch.descripcion = cambios.descripcion || null;
      if ("nombre" in cambios && cambios.nombre?.trim()) patch.nombre = cambios.nombre.trim();
      if ("fuente" in cambios) patch.fuente = claveFuente(cambios.fuente, fuentes);
      if ("entidadRelacionadaId" in cambios)
        patch.id_entidad_relacionada = cambios.entidadRelacionadaId ? Number(cambios.entidadRelacionadaId) : null;
      if ("propiedadId" in cambios)
        patch.id_propiedad = cambios.propiedadId ? Number(cambios.propiedadId) : null;

      if (Object.keys(patch).length) {
        const { error } = await sb.from("tickets").update(patch).eq("id", Number(id));
        if (error) {
          toast.error(`No se pudo actualizar: ${error.message}`);
          invalidate("tickets-list");
          return false;
        }
      }
      // La RPC ya asentó el cambio de categoría: la nota no se duplica.
      if (nota && !reclasificadoEnBd) await registrarActividad(id, nota, "cambio_estado");
      if ("propietarios" in cambios) {
        logger.registrarAsignacion(
          "ticket",
          { id_ticket: Number(id), propietarios: cambios.propietarios },
          "asignar_ticket",
        );
      } else {
        logger.registrarActualizacion(
          "ticket",
          { id_ticket: Number(id) },
          { id_ticket: Number(id), ...cambios },
          "actualizar_ticket",
        );
      }
      invalidate("tickets-list");
      return true;
    },
    [tickets, pipelines, categorias, fuentes, registrarActividad, invalidate, logger, notificarAsignacion, pipelineNombre],
  );

  // Propiedades ligadas desde el detalle. La tabla de unión manda; tickets.id_propiedad /
  // id_cuenta_cobranza siguen a la primera (las EFs y la app de clientes leen esas columnas).
  // `inmueble` NO se toca aquí: en el admin es el "Proyecto" que se edita con su propio select.
  // id_proyecto solo se llena si estaba vacío.
  const guardarPropiedadesTicket = useCallback(
    async (id: string, propiedades: PropiedadRef[]): Promise<boolean> => {
      const tk = tickets.find((t) => t.id === id);
      const lista = dedupePropiedades(propiedades);
      const previas = tk?.propiedades ?? [];
      const r = await sincronizarPropiedadesTicket(id, lista);
      if (typeof r === "object") {
        toast.error(`No se pudieron guardar las propiedades: ${r.error}`);
        return false;
      }
      if (r === "sin_tabla" && lista.length > 1) {
        toast.error("Ligar varias propiedades aún no está disponible en este ambiente. Deja solo una.");
        return false;
      }
      const principal = lista[0] ?? null;
      const patch: Record<string, unknown> = { id_propiedad: principal ? Number(principal.id) : null };
      if (await existeColumnaCuentaCobranza())
        patch.id_cuenta_cobranza = principal?.idCuentaCobranza ? Number(principal.idCuentaCobranza) : null;
      if (principal?.idProyecto) {
        const { data: actual } = await sb.from("tickets").select("id_proyecto").eq("id", Number(id)).maybeSingle();
        if (actual && actual.id_proyecto == null) patch.id_proyecto = Number(principal.idProyecto);
      }
      // Sin tabla y con una sola propiedad: basta con la columna, como antes.
      const { error } = await sb.from("tickets").update(patch).eq("id", Number(id));
      if (error) {
        toast.error(`No se pudo actualizar la propiedad principal: ${error.message}`);
        invalidate("tickets-list");
        return false;
      }
      const agregadas = lista.filter((p) => !previas.some((x) => x.id === p.id));
      const quitadas = previas.filter((p) => !lista.some((x) => x.id === p.id));
      const partes: string[] = [];
      if (agregadas.length) partes.push(`Propiedad agregada: ${agregadas.map((p) => p.etiqueta).join(", ")}.`);
      if (quitadas.length) partes.push(`Propiedad quitada: ${quitadas.map((p) => p.etiqueta).join(", ")}.`);
      if (partes.length) await registrarActividad(id, partes.join(" "), "cambio_estado");
      logger.registrarActualizacion(
        "ticket",
        { id_ticket: Number(id), propiedades: previas.map((p) => Number(p.id)) },
        { id_ticket: Number(id), propiedades: lista.map((p) => Number(p.id)) },
        "actualizar_propiedades_ticket",
      );
      invalidate("tickets-list");
      return true;
    },
    [tickets, registrarActividad, invalidate, logger],
  );

  const relacionarTicket = useCallback(
    async (id: string, relacionado: { id: string; numero: number } | null): Promise<boolean> => {
      if (!(await existeColumnaTicketRelacionado())) {
        toast.error("Relacionar tickets aún no está disponible en este ambiente.");
        return false;
      }
      const { error } = await sb
        .from("tickets")
        .update({ id_ticket_relacionado: relacionado ? Number(relacionado.id) : null })
        .eq("id", Number(id));
      if (error) {
        toast.error(`No se pudo relacionar el ticket: ${mensajeErrorTicketRelacionado(error)}`);
        return false;
      }
      await registrarActividad(
        id,
        relacionado ? `Relacionado con el ticket #${relacionado.numero}.` : "Se quitó la relación con el ticket anterior.",
        "cambio_estado",
      );
      logger.registrarActualizacion(
        "ticket",
        { id_ticket: Number(id) },
        { id_ticket: Number(id), id_ticket_relacionado: relacionado ? Number(relacionado.id) : null },
        "relacionar_ticket",
      );
      invalidate("tickets-list");
      invalidate("tickets-relacionados");
      return true;
    },
    [registrarActividad, invalidate, logger],
  );

  const agregarPropiedadTicket = useCallback(
    (id: string, propiedad: PropiedadRef) => {
      const actuales = tickets.find((t) => t.id === id)?.propiedades ?? [];
      if (actuales.some((p) => p.id === propiedad.id)) return Promise.resolve(true);
      return guardarPropiedadesTicket(id, [...actuales, propiedad]);
    },
    [tickets, guardarPropiedadesTicket],
  );

  const quitarPropiedadTicket = useCallback(
    (id: string, propiedadId: string) => {
      const actuales = tickets.find((t) => t.id === id)?.propiedades ?? [];
      return guardarPropiedadesTicket(
        id,
        actuales.filter((p) => p.id !== propiedadId),
      );
    },
    [tickets, guardarPropiedadesTicket],
  );

  // «Yo trabajo el ticket»: quien lo toma queda como único propietario (sale de «Mis tickets»
  // de los demás y de «Sin asignar»), el ticket pasa a la etapa de atención y queda una nota de
  // seguimiento. Soltarlo solo quita al responsable: el ticket se queda en su etapa (un ticket no
  // regresa a una etapa anterior) y queda libre para que otro lo tome.
  // `auto`: lo dispara el trabajo mismo (mover a atención, dejar una nota): solo toma un ticket
  // que nadie atiende y, si otra persona ya lo tiene, no hace nada ni avisa con error.
  const tomarTicket = useCallback(
    async (id: string, tomar: boolean, auto = false): Promise<boolean> => {
      const tk = tickets.find((t) => t.id === id);
      if (!tk || !uid) return false;
      const destinoAtencion = tomar ? etapaAtencion(etapas, tk.pipelineId) : undefined;
      // Primero se lee lo vigente: si otra persona lo tomó hace un momento, no se le quita.
      const { data: actuales, error: leerErr } = await sb
        .from("tickets_propietarios")
        .select("id_usuario")
        .eq("id_ticket", Number(id));
      if (leerErr) {
        toast.error(`No se pudo tomar el ticket: ${leerErr.message}`);
        return false;
      }
      const vigentes = (actuales ?? []).map((p: any) => p.id_usuario as string);
      const { data: tkActual } = await sb.from("tickets").select("id_etapa").eq("id", Number(id)).maybeSingle();
      // Etapa vigente en BD: la de la lista puede venir de antes de un movimiento recién hecho.
      const etapaActual = String(tkActual?.id_etapa ?? tk.etapaId);
      const otro = atendidoPor({ propietarios: vigentes, etapaId: etapaActual, pipelineId: tk.pipelineId }, etapas);
      if (auto && otro) return false; // ya lo atiende alguien (otra persona o uno mismo)
      if (tomar && otro && otro !== uid) {
        const quien = agentes.find((a) => a.id === otro)?.nombre ?? "Otra persona";
        toast.error(`${quien} ya está atendiendo este ticket.`);
        invalidate("tickets-list");
        return false;
      }

      await sb.from("tickets_propietarios").delete().eq("id_ticket", Number(id));
      if (tomar) {
        const { error } = await sb.from("tickets_propietarios").insert({ id_ticket: Number(id), id_usuario: uid });
        if (error) {
          toast.error(`No se pudo tomar el ticket: ${error.message}`);
          return false;
        }
      }
      const patch: Record<string, unknown> = { id_usuario_propietario: tomar ? uid : null };
      // Tomar lleva a «En atención» solo si es avanzar: un ticket más adelante se queda donde está.
      const origenEtapa = etapas.find((e) => e.id === etapaActual);
      const destino =
        destinoAtencion && destinoAtencion.id !== etapaActual && puedeMoverA(origenEtapa, destinoAtencion)
          ? destinoAtencion
          : undefined;
      if (destino) {
        patch.id_etapa = Number(destino.id);
        patch.fecha_cierre = null;
      }
      const { error } = await sb.from("tickets").update(patch).eq("id", Number(id));
      if (error) {
        if (esErrorNoRetrocede(error)) {
          console.error("[portal-tickets] tomarTicket rechazado por ticket_no_retrocede:", error);
          toast.error(TOAST_NO_RETROCEDE);
        } else toast.error(`No se pudo actualizar el ticket: ${error.message}`);
        invalidate("tickets-list");
        return false;
      }
      if (destino)
        await registrarActividad(id, `Etapa actualizada a "${destino.nombre}".`, "cambio_estado", null, false, {
          origen: etapaActual,
          destino: destino.id,
        });
      const nombre = autor.split(" ")[0] || autor;
      const nota = tomar ? `${nombre} estará atendiendo el ticket.` : `${nombre} dejó de atender el ticket.`;
      await registrarActividad(id, nota, "nota");
      logger.registrarAsignacion(
        "ticket",
        { id_ticket: Number(id), propietarios: tomar ? [uid] : [] },
        tomar ? "tomar_ticket" : "soltar_ticket",
      );
      if (auto) toast.info("Quedaste atendiendo este ticket: los demás ya no lo ven en sus pendientes.");
      invalidate("tickets-list");
      return true;
    },
    [tickets, etapas, agentes, uid, autor, registrarActividad, invalidate, logger],
  );

  const moverEtapa = useCallback(
    async (id: string, etapaId: string, seguimiento?: SeguimientoResolucion): Promise<boolean> => {
      const etapa = etapas.find((e) => e.id === etapaId);
      const tk = tickets.find((t) => t.id === id);
      if (!etapa) return false;
      const origen = etapas.find((e) => e.id === tk?.etapaId);
      // Sin retroceso: la regla vive también en BD (trigger ticket_no_retrocede); aquí se corta
      // antes de subir evidencia o notas que quedarían colgadas.
      if (!puedeMoverA(origen, etapa)) {
        toast.error(TOAST_NO_RETROCEDE);
        return false;
      }

      // Resolver sin seguimiento no pasa: la regla vive aquí para que ningún botón la salte.
      if (requiereSeguimiento(origen, etapa)) {
        const texto = seguimiento?.texto.trim() ?? "";
        const archivos = seguimiento?.archivos ?? [];
        if (!texto && archivos.length === 0) {
          toast.error("Para resolver el ticket deja un mensaje de seguimiento o una evidencia.");
          return false;
        }
        if (archivos.length) await saveTicketAdjuntos(id, uid, archivos, { visibleCliente: seguimiento?.visibleCliente });
        if (texto) {
          const ok = await registrarActividad(id, texto, "nota", null, seguimiento?.visibleCliente ?? false);
          if (!ok) return false;
          if (tk) notificarNota(tk, texto);
        }
      }

      const { error } = await sb
        .from("tickets")
        .update({
          id_etapa: Number(etapaId),
          id_pipeline: Number(etapa.pipelineId),
          fecha_cierre: etapa.cerrada ? new Date().toISOString() : null,
        })
        .eq("id", Number(id));
      if (error) {
        if (esErrorNoRetrocede(error)) {
          console.error("[portal-tickets] moverEtapa rechazado por ticket_no_retrocede:", error);
          toast.error(TOAST_NO_RETROCEDE);
          invalidate("tickets-list");
        } else toast.error(`No se pudo mover el ticket: ${error.message}`);
        return false;
      }
      await registrarActividad(id, `Etapa actualizada a "${etapa.nombre}".`, "cambio_estado", null, false, {
        origen: tk?.etapaId ?? null,
        destino: etapaId,
      });
      logger.registrarActualizacion(
        "ticket",
        { id_ticket: Number(id) },
        { id_ticket: Number(id), id_etapa: Number(etapaId), etapa: etapa.nombre },
        etapa.cerrada ? "cerrar_ticket" : "mover_etapa_ticket",
      );
      // Etapa final → avisar por correo a propietarios + creador.
      if (etapa.cerrada && tk) notificarCierre(tk);
      // Moverlo a atención es ponerse a trabajarlo: si nadie lo atiende, queda a nombre de quien lo movió.
      if (etapaAtencion(etapas, etapa.pipelineId)?.id === etapaId) await tomarTicket(id, true, true);
      invalidate("tickets-list");
      return true;
    },
    [etapas, tickets, uid, registrarActividad, invalidate, logger, notificarCierre, notificarNota, tomarTicket],
  );

  // Cambiar el PIPELINE de un ticket. Como la etapa y la categoría son POR pipeline,
  // al mover se reubica el ticket en la primera etapa del pipeline destino y se limpia
  // la categoría (pertenecía al pipeline anterior; el usuario elige una nueva).
  const cambiarPipeline = useCallback(
    async (id: string, pipelineId: string) => {
      const pipeline = pipelines.find((p) => p.id === pipelineId);
      if (!pipeline) return false;
      if (pipeline.soloApps) {
        toast.error(MENSAJE_PIPELINE_SOLO_APPS);
        return false;
      }
      const etapaDestino = etapas
        .filter((e) => e.pipelineId === pipelineId)
        .sort((a, b) => a.orden - b.orden)[0];
      if (!etapaDestino) {
        toast.error(`El pipeline "${pipeline.nombre}" no tiene etapas; agrega una antes de mover tickets ahí.`);
        return false;
      }
      const { error } = await sb
        .from("tickets")
        .update({
          id_pipeline: Number(pipelineId),
          id_etapa: Number(etapaDestino.id),
          id_categoria: null,
          fecha_cierre: etapaDestino.cerrada ? new Date().toISOString() : null,
        })
        .eq("id", Number(id));
      if (error) {
        toast.error(`No se pudo cambiar el pipeline: ${error.message}`);
        return false;
      }
      await registrarActividad(
        id,
        `Pipeline cambiado a "${pipeline.nombre}" (etapa: ${etapaDestino.nombre}).`,
        "cambio_estado",
        null,
        false,
        { origen: tickets.find((t) => t.id === id)?.etapaId ?? null, destino: etapaDestino.id },
      );
      logger.registrarActualizacion(
        "ticket",
        { id_ticket: Number(id) },
        { id_ticket: Number(id), id_pipeline: Number(pipelineId), pipeline: pipeline.nombre },
        "cambiar_pipeline_ticket",
      );
      invalidate("tickets-list");
      return true;
    },
    [pipelines, etapas, tickets, registrarActividad, invalidate, logger],
  );

  // «Guardar cambios» del detalle: UNA llamada a `actualizar_ticket_completo` (todo o nada, misma
  // RLS de siempre). Ya no manda a la BD una cadena de pasos que podía quedar a medias.
  // La etapa terminal desde una de trabajo NO viaja aquí: pide seguimiento y evidencia (se sube
  // desde el front), así que quien llama la mueve aparte con `moverEtapa`; la RPC también la rechaza.
  const guardarTicketCompleto = useCallback(
    async (id: string, cambios: CambiosTicketCompleto, nota?: string): Promise<boolean> => {
      const tk = tickets.find((t) => t.id === id);
      if (cambios.pipelineId && cambios.pipelineId !== tk?.pipelineId) {
        const pipeline = pipelines.find((p) => p.id === cambios.pipelineId);
        if (pipeline?.soloApps) {
          toast.error(MENSAJE_PIPELINE_SOLO_APPS);
          return false;
        }
      }
      if (cambios.etapaId && tk) {
        const destino = etapas.find((e) => e.id === cambios.etapaId);
        const origen = etapas.find((e) => e.id === tk.etapaId);
        if (!puedeMoverA(origen, destino)) {
          toast.error(TOAST_NO_RETROCEDE);
          return false;
        }
        if (requiereSeguimiento(origen, destino)) {
          toast.error("Para resolver el ticket deja un mensaje de seguimiento o una evidencia.");
          return false;
        }
      }

      const payload: Record<string, unknown> = {};
      if (cambios.pipelineId !== undefined) payload.pipeline = Number(cambios.pipelineId);
      if (cambios.etapaId !== undefined) payload.etapa = Number(cambios.etapaId);
      if (cambios.categoriaId !== undefined) payload.categoria = cambios.categoriaId ? Number(cambios.categoriaId) : null;
      if (cambios.fuente !== undefined) payload.fuente = claveFuente(cambios.fuente, fuentes);
      if (cambios.prioridad !== undefined) payload.prioridad = cambios.prioridad;
      if (cambios.propietarios !== undefined) payload.propietarios = cambios.propietarios.filter(Boolean);
      if (cambios.solicitantes !== undefined)
        payload.solicitantes = cambios.solicitantes.filter(Boolean).map((c) => ({ id: Number(c.id), nombre: c.nombre }));
      if (cambios.propiedades !== undefined) {
        payload.propiedades = dedupePropiedades(cambios.propiedades).map((p) => ({
          id: Number(p.id),
          idCuentaCobranza: p.idCuentaCobranza ? Number(p.idCuentaCobranza) : null,
          idProyecto: p.idProyecto ? Number(p.idProyecto) : null,
        }));
        if (cambios.notaPropiedades) payload.nota_propiedades = cambios.notaPropiedades;
      }
      if (cambios.descripcion !== undefined) payload.descripcion = cambios.descripcion;
      if (cambios.inmueble !== undefined) payload.inmueble = cambios.inmueble;
      if (!Object.keys(payload).length) return true;

      const { data, error } = await sb.rpc("actualizar_ticket_completo", {
        p_id_ticket: Number(id),
        p_cambios: payload,
        p_nota: nota?.trim() ? nota.trim() : null,
      });

      if (error) {
        // Ambiente sin la migración: la cadena de siempre (varias llamadas, como antes).
        if (error.code === "PGRST202" || error.code === "42883") {
          if (cambios.pipelineId && cambios.pipelineId !== tk?.pipelineId && !(await cambiarPipeline(id, cambios.pipelineId)))
            return false;
          if (
            cambios.categoriaId !== undefined &&
            !(await actualizarTicket(
              id,
              { categoriaId: cambios.categoriaId },
              `Categoría actualizada a "${categorias.find((c) => c.id === cambios.categoriaId)?.nombre}".`,
            ))
          )
            return false;
          const patch: Partial<Ticket> = {};
          if (cambios.prioridad !== undefined) patch.prioridad = cambios.prioridad;
          if (cambios.propietarios !== undefined) patch.propietarios = cambios.propietarios;
          if (cambios.solicitantes !== undefined) patch.solicitantes = cambios.solicitantes;
          if (cambios.fuente !== undefined) patch.fuente = cambios.fuente;
          if (cambios.descripcion !== undefined) patch.descripcion = cambios.descripcion;
          if (cambios.inmueble !== undefined) patch.inmueble = cambios.inmueble;
          if (Object.keys(patch).length && !(await actualizarTicket(id, patch, nota?.trim() || undefined))) return false;
          if (cambios.propiedades !== undefined && !(await guardarPropiedadesTicket(id, cambios.propiedades))) return false;
          if (cambios.etapaId && !(await moverEtapa(id, cambios.etapaId))) return false;
          return true;
        }
        if (esErrorNoRetrocede(error)) {
          console.error("[portal-tickets] guardarTicketCompleto rechazado por ticket_no_retrocede:", error);
          toast.error(TOAST_NO_RETROCEDE);
        } else if (error.code === "42501" || error.code === "22023") {
          // Permisos y datos que no cuadran (RPC y reclasificación de Atención): ya vienen en español.
          toast.error(textoErrorRpc(error));
        } else toast.error(`No se pudieron guardar los cambios: ${textoErrorRpc(error)}`);
        invalidate("tickets-list");
        return false;
      }

      const r = (data ?? {}) as { propietarios_nuevos?: string[]; etapa_cerrada?: boolean; tomado?: boolean };
      const pipelineId = cambios.pipelineId ?? tk?.pipelineId;
      // Avisos de asignación solo a los responsables recién agregados.
      if (r.propietarios_nuevos?.length)
        notificarAsignacion(r.propietarios_nuevos, {
          folio: tk?.numero ?? "",
          nombre: tk?.nombre ?? "Ticket",
          pipeline: pipelineNombre(pipelineId),
          proyecto: cambios.inmueble ?? tk?.inmueble,
          descripcion: cambios.descripcion ?? tk?.descripcion,
        });
      if (r.etapa_cerrada && tk) notificarCierre(tk);
      if (r.tomado) toast.info("Quedaste atendiendo este ticket: los demás ya no lo ven en sus pendientes.");
      if (cambios.propietarios !== undefined)
        logger.registrarAsignacion("ticket", { id_ticket: Number(id), propietarios: cambios.propietarios }, "asignar_ticket");
      logger.registrarActualizacion(
        "ticket",
        { id_ticket: Number(id) },
        { id_ticket: Number(id), ...payload },
        "actualizar_ticket_completo",
      );
      invalidate("tickets-list");
      return true;
    },
    [
      tickets, pipelines, etapas, categorias, fuentes, cambiarPipeline, actualizarTicket, guardarPropiedadesTicket,
      moverEtapa, notificarAsignacion, notificarCierre, pipelineNombre, invalidate, logger,
    ],
  );

  // Palomitas: el equipo abrió la Conversación. Silencioso: es una marca de lectura, no una
  // acción del usuario, y si la migración no está en el ambiente no debe estorbar.
  const marcarLeidoEquipo = useCallback(
    async (id: string) => {
      const { error } = await sb.rpc("tickets_marcar_leido_equipo", { p_id_ticket: Number(id) });
      if (error) {
        console.warn("tickets_marcar_leido_equipo", error.message);
        return;
      }
      invalidate("tickets-list");
    },
    [invalidate],
  );

  const eliminarTickets = useCallback(
    async (ids: string[]) => {
      const { error } = await sb
        .from("tickets")
        .update({ activo: false })
        .in("id", ids.map((i) => Number(i)));
      if (error) {
        toast.error(`No se pudo eliminar: ${error.message}`);
        return false;
      }
      ids.forEach((id) =>
        logger.registrarEliminacion("ticket", { id_ticket: Number(id) }, "eliminar_ticket"),
      );
      invalidate("tickets-list");
      return true;
    },
    [invalidate, logger],
  );

  const agregarNota = useCallback(
    async (id: string, texto: string, audioFile?: File, paraCliente = false): Promise<boolean> => {
      let audio: { url: string; nombre: string | null; mime: string | null } | undefined;
      if (audioFile) {
        const up = await uploadTicketFile(id, audioFile);
        // Sin el audio la nota de voz no dice nada: se detiene en vez de guardar "Nota de voz" vacía.
        if (!up) {
          toast.error("No se pudo subir la nota de voz.");
          return false;
        }
        audio = { url: up.url, nombre: up.nombre, mime: up.mime };
      }
      const ok = await registrarActividad(id, texto || "Nota de voz", "nota", audio, paraCliente);
      if (!ok) return false;
      const tk = tickets.find((t) => t.id === id);
      if (tk) notificarNota(tk, texto || "Nota de voz");
      // Dejar seguimiento es trabajar el ticket: si está abierto y nadie lo atiende, queda tomado
      // por quien escribió (pasa a atención y deja de aparecerle a los demás).
      if (tk && !esEtapaTerminal(etapas.find((e) => e.id === tk.etapaId))) await tomarTicket(id, true, true);
      invalidate("tickets-list");
      return true;
    },
    [registrarActividad, invalidate, tickets, etapas, notificarNota, tomarTicket],
  );

  // ─── Catálogos (upsert por id vacío = insert / id existente = update) ─────────
  const guardarPipeline = useCallback(
    async (p: Pipeline) => {
      const payload = { nombre: p.nombre.trim(), descripcion: p.descripcion?.trim() || null };
      const { error } = p.id
        ? await sb.from("tickets_pipelines").update(payload).eq("id", Number(p.id))
        : await sb.from("tickets_pipelines").insert(payload);
      if (error) return toast.error(error.message);
      invalidate("tickets-pipelines");
    },
    [invalidate],
  );

  const eliminarPipeline = useCallback(
    async (id: string) => {
      const { error } = await sb.from("tickets_pipelines").update({ activo: false }).eq("id", Number(id));
      if (error) return toast.error(error.message);
      invalidate("tickets-pipelines");
      invalidate("tickets-etapas");
    },
    [invalidate],
  );

  const guardarEtapa = useCallback(
    async (e: Etapa) => {
      const payload = {
        id_pipeline: Number(e.pipelineId),
        nombre: e.nombre.trim(),
        orden: Number(e.orden) || 100,
        cerrada: !!e.cerrada,
        // NULL = lo deriva el trigger trg_tickets_etapas_tipo (tipo obligatorio en BD).
        tipo_semantico: e.tipoSemantico ?? null,
      };
      const { error } = e.id
        ? await sb.from("tickets_etapas").update(payload).eq("id", Number(e.id))
        : await sb.from("tickets_etapas").insert(payload);
      if (error) return toast.error(error.message);
      invalidate("tickets-etapas");
    },
    [invalidate],
  );

  const eliminarEtapa = useCallback(
    async (id: string) => {
      const { error } = await sb.from("tickets_etapas").update({ activo: false }).eq("id", Number(id));
      if (error) return toast.error(error.message);
      invalidate("tickets-etapas");
    },
    [invalidate],
  );

  const guardarCategoria = useCallback(
    async (c: Categoria) => {
      const payload = { nombre: c.nombre.trim() };
      const { error } = c.id
        ? await sb.from("tickets_categorias").update(payload).eq("id", Number(c.id))
        : await sb.from("tickets_categorias").insert(payload);
      if (error) return toast.error(error.message);
      invalidate("tickets-categorias");
    },
    [invalidate],
  );

  const eliminarCategoria = useCallback(
    async (id: string) => {
      const { error } = await sb.from("tickets_categorias").update({ activo: false }).eq("id", Number(id));
      if (error) return toast.error(error.message);
      invalidate("tickets-categorias");
    },
    [invalidate],
  );

  // El "Equipo" son usuarios reales de la plataforma: no se crean/eliminan desde aquí.
  const avisoUsuarios = useCallback(
    () => toast.info("El equipo se administra desde el módulo de Usuarios de la plataforma."),
    [],
  );

  const value = useMemo<Store>(
    () => {
      const store: Store = {
        cerradosDesde,
        hayMasCerrados: !!cerradosDesde && masCerradosQ.data === true,
        verMasCerrados: () => setMesesCerrados((m) => m + 1),
        cargarTodosCerrados: setTodosCerrados,
        soloLectura: verComo,
        tickets,
        pipelines,
        etapas,
        categorias,
        agentes,
        fuentes,
        etiquetaFuente,
        autor,
        cargando:
          pipelinesQ.isLoading || etapasQ.isLoading || categoriasQ.isLoading || ticketsQ.isLoading,
        recargando: ticketsQ.isPlaceholderData,
        errorCarga: ticketsQ.isError || pipelinesQ.isError || etapasQ.isError,
        recargar: () => {
          ticketsQ.refetch();
          pipelinesQ.refetch();
          etapasQ.refetch();
          categoriasQ.refetch();
        },
        alcance,
        crearTicket,
        actualizarTicket,
        guardarPropiedadesTicket,
        guardarTicketCompleto,
        agregarPropiedadTicket,
        quitarPropiedadTicket,
        relacionarTicket,
        moverEtapa,
        tomarTicket,
        cambiarPipeline,
        marcarLeidoEquipo,
        eliminarTickets,
        agregarNota,
        guardarPipeline,
        eliminarPipeline,
        guardarEtapa,
        eliminarEtapa,
        guardarCategoria,
        eliminarCategoria,
        guardarAgente: avisoUsuarios,
        eliminarAgente: avisoUsuarios,
      };
      return verComo ? conEscriturasBloqueadas(store) : store;
    },
    [
      verComo,
      tickets,
      pipelines,
      etapas,
      categorias,
      agentes,
      fuentes,
      etiquetaFuente,
      autor,
      pipelinesQ.isLoading,
      etapasQ.isLoading,
      categoriasQ.isLoading,
      cerradosDesde,
      masCerradosQ.data,
      ticketsQ.isLoading,
      ticketsQ.isPlaceholderData,
      ticketsQ.isError,
      pipelinesQ.isError,
      etapasQ.isError,
      ticketsQ.refetch,
      pipelinesQ.refetch,
      etapasQ.refetch,
      categoriasQ.refetch,
      alcance,
      crearTicket,
      actualizarTicket,
      guardarPropiedadesTicket,
      guardarTicketCompleto,
      agregarPropiedadTicket,
      quitarPropiedadTicket,
      relacionarTicket,
      moverEtapa,
      tomarTicket,
      cambiarPipeline,
      marcarLeidoEquipo,
      eliminarTickets,
      agregarNota,
      guardarPipeline,
      eliminarPipeline,
      guardarEtapa,
      eliminarEtapa,
      guardarCategoria,
      eliminarCategoria,
      avisoUsuarios,
    ],
  );

  return <TicketsContext.Provider value={value}>{children}</TicketsContext.Provider>;
}

export function useTickets() {
  const ctx = useContext(TicketsContext);
  if (!ctx) throw new Error("useTickets debe usarse dentro de TicketsProvider");
  return ctx;
}

/**
 * Igual que useTickets pero sin exigir el provider: devuelve null fuera de TicketsProvider.
 * Lo usan piezas del Portal Tickets que también se montan en otros portales (p. ej. la
 * configuración de pipelines en Cobranza y Productos), donde no hay tickets cargados.
 */
export function useTicketsOpcional() {
  return useContext(TicketsContext);
}
